import { useState, useEffect, useRef, useTransition, useMemo, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  Users, Search, Settings, Pencil, ClipboardList, Coins, Trash2, ShoppingCart,
  CheckCircle, Check, Banknote, CreditCard, Building2, BookOpen, Plus, ScrollText,
  FileSpreadsheet, Download, Upload, Lightbulb, CircleDot, ArrowLeft, User,
  Phone, Mail, MapPin, IdCard, CalendarDays, Store, Menu, ChevronDown, ChevronRight, Scale, AlertTriangle, Printer,
  ExternalLink, Calendar
} from 'lucide-react';
import html2pdf from 'html2pdf.js';
import LogModal from '../components/LogModal';
import PriceInput from '../components/PriceInput';
import logService from '../services/logService';
import { API_URL, API_HOST } from '../config/api';
import useEscapeClose from '../hooks/useEscapeClose';
import { handlePrint } from '../utils/printHelper';
import { normalizePhoneDigits, formatPhoneDisplay, isPhoneComplete } from '../utils/phoneFormat';
import './Customers.css';
import './RetailSales.css';

// Türkçe karakter duyarsız küçük harfe çevirme
const turkishToLower = (str) => {
  if (!str) return '';
  return str.toLocaleLowerCase('tr-TR');
};

// Türkçe karakter uyumlu metin içerme kontrolü
const turkishIncludes = (text, search) => {
  return turkishToLower(text).includes(turkishToLower(search));
};

// Havale/EFT hesap adı gösterilirken (Şirket)/(Şahsi) etiketini gizle — hesap adı zaten türü belirtiyor
const stripBankAccountType = (label) => (label || '').replace(/\s*\((Şirket|Şahsi|Şahıs|E-Ticaret)\)\s*$/i, '');

const Customers = ({ user, selectedStore }) => {
  const navigate = useNavigate();

  // Sol Menü
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [menuAccordion, setMenuAccordion] = useState({
    customers: true,
    excel: true
  });

  // Menü toggle
  const toggleSideMenu = () => {
    startTransition(() => {
      setSideMenuOpen(prev => !prev);
    });
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCustomer, setNewCustomer] = useState({ name: '', store_type: '', phone: '', email: '', address: '', tax_number: '', birthday: '', sms_consent: false, kvkk_consent: false });
  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [existingCustomerByPhone, setExistingCustomerByPhone] = useState(null); // Telefon numarasına göre mevcut müşteri

  // Satış geçmişi için
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSales, setCustomerSales] = useState([]);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [loadingSales, setLoadingSales] = useState(false);
  const [salesModalTab, setSalesModalTab] = useState('sales'); // 'sales', 'payments', 'balance'
  const [expandedHistoryItems, setExpandedHistoryItems] = useState(new Set());
  const [lightboxImage, setLightboxImage] = useState(null);
  const [saleDetailModal, setSaleDetailModal] = useState(null); // fiş detay modal
  const [saleDetailLoading, setSaleDetailLoading] = useState(false);

  // Tahsilat geçmişi için
  const [customerPayments, setCustomerPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Düzenleme için
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [originalCustomer, setOriginalCustomer] = useState(null);

  // Toplu silme için (inline seçim)
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);

  // Borç takibi için
  const [debtSummary, setDebtSummary] = useState({});
  const [creditSummary, setCreditSummary] = useState({});
  const [showDebtModal, setShowDebtModal] = useState(false);
  const [debtModalTab, setDebtModalTab] = useState('debts');
  const [customerDebts, setCustomerDebts] = useState([]);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [gramBalanceHistory, setGramBalanceHistory] = useState([]);
  const [customerBalance, setCustomerBalance] = useState(0);
  const [gramBalance, setGramBalance] = useState(0);
  const [gumusBalance, setGumusBalance] = useState(0);
  const [loadingDebts, setLoadingDebts] = useState(false);
  const [selectedDebtGroup, setSelectedDebtGroup] = useState(null); // Grup ödeme için
  const [showReportModal, setShowReportModal] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);

  // Borçları türe göre grupla ve topla
  const groupedCustomerDebts = useMemo(() => {
    if (!customerDebts || customerDebts.length === 0) return [];

    const groups = {};

    customerDebts.forEach(debt => {
      const type = debt.debt_type || 'TL';
      if (!groups[type]) {
        groups[type] = {
          debt_type: type,
          debts: [],
          total_amount: 0,
          total_amount_tl: 0,
          debt_ids: []
        };
      }
      groups[type].debts.push(debt);
      groups[type].debt_ids.push(debt.id);
      groups[type].total_amount += parseFloat(debt.amount) || 0;
      groups[type].total_amount_tl += parseFloat(debt.amount_tl) || 0;
    });

    // Array'e çevir ve sırala (TL, GRAM, GUMUS)
    const order = ['TL', 'GRAM', 'GUMUS'];
    return Object.values(groups).sort((a, b) => order.indexOf(a.debt_type) - order.indexOf(b.debt_type));
  }, [customerDebts]);

  // Türkçe karakter duyarsız filtrelenmiş müşteriler (alakalılığa göre sıralı)
  // Arama kutusu yazılırken/silinirken tıkanmayı önlemek için: filtre/sıralama, ertelenmiş
  // (deferred) arama terimiyle hesaplanır — böylece her tuş vuruşu ana iş parçacığını kilitlemez.
  const deferredSearch = useDeferredValue(searchTerm);
  const filteredCustomers = useMemo(() => {
    const term = (deferredSearch || '').trim();
    if (!term) return customers;
    const filtered = customers.filter(customer => {
      const nameMatch = turkishIncludes(customer.name || '', term);
      const phoneMatch = (customer.phone || '').includes(term);
      const emailMatch = turkishIncludes(customer.email || '', term);
      return nameMatch || phoneMatch || emailMatch;
    });
    const qLower = turkishToLower(term);
    filtered.sort((a, b) => {
      const aName = turkishToLower(a.name || '');
      const bName = turkishToLower(b.name || '');
      const aStarts = aName.startsWith(qLower);
      const bStarts = bName.startsWith(qLower);
      const aWordStarts = aName.split(' ').some(w => w.startsWith(qLower));
      const bWordStarts = bName.split(' ').some(w => w.startsWith(qLower));
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      if (aWordStarts && !bWordStarts) return -1;
      if (!aWordStarts && bWordStarts) return 1;
      const aIdx = aName.indexOf(qLower);
      const bIdx = bName.indexOf(qLower);
      if (aIdx !== bIdx) return aIdx - bIdx;
      return aName.localeCompare(bName, 'tr');
    });
    return filtered;
  }, [customers, deferredSearch]);

  // Ekrana basılan satır sayısını sınırla (aşırı geniş eşleşmede DOM'u kilitlememek için).
  const MAX_RENDERED_ROWS = 300;
  const visibleCustomers = filteredCustomers.length > MAX_RENDERED_ROWS
    ? filteredCustomers.slice(0, MAX_RENDERED_ROWS)
    : filteredCustomers;

  // Ödeme modalı için
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [mixedPayments, setMixedPayments] = useState([]); // Karışık ödemeler için
  const [paymentInputMode, setPaymentInputMode] = useState('tl'); // 'tl' veya 'gram' - altın borcu için
  const [bankAccounts, setBankAccounts] = useState([]); // Banka hesapları
  const [selectedBankAccount, setSelectedBankAccount] = useState(null); // Seçili banka hesabı
  const [customRates, setCustomRates] = useState({});  // Özel kurlar (makas farkı)
  const [salesStaffList, setSalesStaffList] = useState([]);
  const [collectionStaffId, setCollectionStaffId] = useState('');
  const [collectionStaffName, setCollectionStaffName] = useState('');
  const [showCollectionStaffModal, setShowCollectionStaffModal] = useState(false);

  // Hurda ile ödeme
  const [scrapPaymentMode, setScrapPaymentMode] = useState(false);
  const [scrapPaymentData, setScrapPaymentData] = useState({
    gross_weight: '',
    waste_percent: '',
    waste_gram: '',
    purity: '',
    custom_purity_rate: null, // Tek seferlik özel oran
    unit_price: ''
  });

  // Hurda manuel toplam düzenleme
  const [isEditingScrapTotal, setIsEditingScrapTotal] = useState(false);
  const [editingScrapTotalValue, setEditingScrapTotalValue] = useState('');
  const [customScrapTotal, setCustomScrapTotal] = useState(null);

  // Hurda ayar oranları - localStorage'dan al
  const defaultPurityRates = {
    gold: { '24': 0.985, '22': 0.900, '21': 0.800, '18': 0.700, '14': 0.500, '10': 0.350, '9': 0.300, '8': 0.250 },
    silver: { '999': 0.999, '925': 0.925, '900': 0.900, '800': 0.800 }
  };
  const [scrapPurityRates, setScrapPurityRates] = useState(defaultPurityRates);

  // Ayar oranı düzenleme state'leri
  const [isEditingPurityRate, setIsEditingPurityRate] = useState(false);
  const [editingPurityRateValue, setEditingPurityRateValue] = useState('');

  // Ayar oranını güncelle (tek seferlik - localStorage'a kaydetmez)
  const updatePurityRate = (metalType, purity, newRate) => {
    const rate = parseFloat(newRate) / 1000;
    if (isNaN(rate) || rate < 0 || rate > 1) return;

    // Sadece formData'daki custom_purity_rate'i güncelle (kalıcı değil)
    setScrapPaymentData(prev => ({ ...prev, custom_purity_rate: rate }));
  };

  // Excel import için
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  // Excel ön izleme için
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState(null);
  const [parsedCustomers, setParsedCustomers] = useState([]);

  // Infinite scroll için
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const ITEMS_PER_PAGE = 50;
  const loadMoreRef = useRef(null);

  // Sıralama için
  const [sortByDebt, setSortByDebt] = useState(false);
  const [sortByCredit, setSortByCredit] = useState(false);
  const [sortByBalance, setSortByBalance] = useState(''); // '' | 'debt_first' | 'credit_first'

  // Log modal için
  const [showLogModal, setShowLogModal] = useState(false);

  // Yardım modal için
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Modal kapanış animasyonu için state
  const [closingModal, setClosingModal] = useState(null);

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Modal kapanış handler'ı (callback opsiyonel)
  const handleCloseModal = (modalName, setShowModal, callback) => {
    setClosingModal(modalName);
    setTimeout(() => {
      setShowModal(false);
      setClosingModal(null);
      if (callback) callback();
    }, 250);
  };

  // Shopify müşterileri için
  const [showShopifyCustomers, setShowShopifyCustomers] = useState(false);
  const [shopifyCustomers, setShopifyCustomers] = useState([]);
  const [shopifyCustomersCount, setShopifyCustomersCount] = useState(0);
  const [loadingShopifyCustomers, setLoadingShopifyCustomers] = useState(false);
  const [shopifySearchTerm, setShopifySearchTerm] = useState('');

  // Altın/Gümüş müşteri filtreleme için
  const [storeTypeFilter, setStoreTypeFilter] = useState('all'); // 'all', 'Altın', 'Gümüş'
  const [goldCustomersCount, setGoldCustomersCount] = useState(0);
  const [silverCustomersCount, setSilverCustomersCount] = useState(0);

  // Kullanıcı bilgisi
  const isAdmin = user?.role === 'admin';
  const isCashier = user?.role === 'cashier';
  const userStoreName = user?.store_name || '';
  // Mağaza adında "gümüş" geçiyorsa gümüş müşterilerini gösterir
  const isSilverStore = userStoreName.toLowerCase().includes('gümüş');

  // Kullanıcının mağazasına göre müşteri tipini belirle
  // Sadece admin tüm müşterileri görebilir, kasiyerler her zaman kendi mağazasının müşterilerini görür
  const getUserStoreType = () => {
    // Sadece admin her şeyi görebilir
    if (isAdmin) return null;
    // Kasiyer ise store_id'ye göre filtrele (canEditAllStores müşteriler için geçerli değil)
    const storeId = Number(user?.store_id);
    if (storeId === 1) return 'Altın';
    if (storeId === 2 || storeId === 3) return 'Gümüş';
    return null;
  };
  const userStoreType = getUserStoreType();

  // Anlık fiyatlar için
  const [prices, setPrices] = useState(null);

  // Sütun görünürlüğü ayarları
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const defaultColumns = {
    name: true,
    phone: true,
    email: true,
    address: false,
    taxNumber: false,
    birthday: false,
    balance: true,
    gramDebt: true,
    createdAt: true
  };
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('customersVisibleColumns');
    return saved ? JSON.parse(saved) : defaultColumns;
  });

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showHelpModal, () => setShowHelpModal(false)],
    [showLogModal, () => {}], // LogModal handles its own ESC
    [lightboxImage, () => setLightboxImage(null)],
    [showCollectionStaffModal, () => setShowCollectionStaffModal(false)],
    [showPaymentModal, () => setShowPaymentModal(false)],
    [showReportModal, () => setShowReportModal(false)],
    [showImportPreview, () => setShowImportPreview(false)],
    [showDebtModal, () => setShowDebtModal(false)],
    [showEditModal, () => setShowEditModal(false)],
    [showSalesModal, () => setShowSalesModal(false)],
    [showKvkkModal, () => setShowKvkkModal(false)],
    [showModal, () => setShowModal(false)],
  ]);

  // Tüm sütun tanımları
  const allColumns = [
    { key: 'name', label: 'Ad Soyad', default: true },
    { key: 'phone', label: 'Telefon', default: true },
    { key: 'email', label: 'E-posta', default: true },
    { key: 'address', label: 'Adres', default: false },
    { key: 'taxNumber', label: 'Vergi No', default: false },
    { key: 'birthday', label: 'Doğum Tarihi', default: false },
    { key: 'balance', label: 'TL Bakiye', default: true },
    { key: 'gramDebt', label: 'Gram Borç', default: true },
    { key: 'createdAt', label: 'Kayıt Tarihi', default: true }
  ];

  // Sütun görünürlüğü değiştir
  const toggleColumn = (columnKey) => {
    const newColumns = { ...visibleColumns, [columnKey]: !visibleColumns[columnKey] };
    setVisibleColumns(newColumns);
    localStorage.setItem('customersVisibleColumns', JSON.stringify(newColumns));
  };

  // Tüm sütunları sıfırla
  const resetColumns = () => {
    setVisibleColumns(defaultColumns);
    localStorage.setItem('customersVisibleColumns', JSON.stringify(defaultColumns));
  };

  // Müşterileri API'den yükle
  useEffect(() => {
    // Kasiyer ise kendi mağaza tipine göre filtrele
    if (userStoreType) {
      setStoreTypeFilter(userStoreType);
      fetchCustomers(1, true, '', false, userStoreType);
      fetchDebtSummary(userStoreType);
      fetchCreditSummary(userStoreType);
    } else {
      fetchCustomers(1, true, '');
      fetchDebtSummary('all');
      fetchCreditSummary('all');
    }
    fetchShopifyCustomersCount();
    fetchStoreTypeCounts();
    fetchCustomRates();
    fetchPrices();
  }, []);

  // Özel kurları getir (makas farkı)
  const fetchCustomRates = async () => {
    try {
      const response = await fetch(`${API_URL}/custom-rates`);
      if (response.ok) {
        const data = await response.json();
        const ratesObj = {};
        data.forEach(rate => {
          ratesObj[rate.currency] = rate;
        });
        setCustomRates(ratesObj);
      }
    } catch (error) {
      console.error('Özel kur getirme hatası:', error);
    }
  };

  // Arama modunda mıyız? (arama kutusuna >=2 karakter yazıldı → tüm liste yerine eşleşenler)
  const searchActiveRef = useRef(false);

  // Arama: >=2 karakterde sunucu taraflı arama; kutu boşaltılınca normal sayfalı listeye dön.
  useEffect(() => {
    const term = (searchTerm || '').trim();
    if (term.length >= 2) {
      const delaySearch = setTimeout(() => {
        const effectiveStoreType = userStoreType || storeTypeFilter;
        searchActiveRef.current = true;
        fetchAllCustomersForSearch(effectiveStoreType, term);
      }, 300);
      return () => clearTimeout(delaySearch);
    }
    // 0-1 karakter: daha önce arama yaptıysak sayfalı listeyi geri yükle (aksi hâlde
    // binlerce müşteri state'te kalıp kısa sorguda ekranı kilitliyordu).
    if (searchActiveRef.current) {
      searchActiveRef.current = false;
      const delayRestore = setTimeout(() => {
        const effectiveStoreType = userStoreType || storeTypeFilter;
        fetchCustomers(1, true, '', sortByDebt, effectiveStoreType, sortByCredit, sortByBalance);
      }, 150);
      return () => clearTimeout(delayRestore);
    }
  }, [searchTerm]);

  // Arama için eşleşen müşterileri çek (sunucu taraflı filtre — tüm tabloyu indirmez)
  const fetchAllCustomersForSearch = async (storeType = 'all', term = '') => {
    try {
      setLoading(true);
      const storeTypeParam = storeType && storeType !== 'all' ? `&storeType=${encodeURIComponent(storeType)}` : '';
      const searchParam = term ? `&search=${encodeURIComponent(term)}` : '';
      const response = await fetch(`${API_URL}/customers?page=1&limit=1000${searchParam}${storeTypeParam}`);
      const data = await response.json();
      setCustomers(data.customers || data);
      setHasMore(false); // Arama sonuçları tek sayfada
    } catch (error) {
      console.error('Müşteri yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mağaza tipi filtresi değiştiğinde yeniden yükle
  useEffect(() => {
    if (storeTypeFilter !== 'all') {
      setShowShopifyCustomers(false);
      fetchCustomers(1, true, searchTerm, sortByDebt, storeTypeFilter, sortByCredit, sortByBalance);
      fetchDebtSummary(storeTypeFilter);
      fetchCreditSummary(storeTypeFilter);
    } else {
      fetchCustomers(1, true, searchTerm, sortByDebt, 'all', sortByCredit, sortByBalance);
      fetchDebtSummary('all');
      fetchCreditSummary('all');
    }
  }, [storeTypeFilter]);

  // IntersectionObserver ile infinite scroll (callback ref)
  const observerRef = useRef(null);
  const sentinelCallback = useRef((node) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && loadMoreRef.current) {
          loadMoreRef.current();
        }
      },
      { rootMargin: '300px' }
    );
    observerRef.current.observe(node);
  });

  // Toplam borç hesaplama (tüm müşteriler)
  const totalDebts = useMemo(() => {
    const summaryValues = Object.values(debtSummary);
    const totalTL = summaryValues.reduce((sum, d) => sum + (parseFloat(d.total_tl_debt) || 0), 0);
    const totalGold = summaryValues.reduce((sum, d) => sum + (parseFloat(d.total_gold_debt) || 0), 0);
    const totalSilver = summaryValues.reduce((sum, d) => sum + (parseFloat(d.total_silver_debt) || 0), 0);

    const calcRate = (isGold) => {
      const currency = isGold ? 'GOLD' : 'SILVER';
      const cr = customRates[currency];
      const liveRate = isGold
        ? parseFloat(prices?.altin_satis) || 0
        : parseFloat(prices?.gumus_satis) || 0;
      if (cr?.satis_percentage != null && cr.satis_percentage !== '' && !isNaN(cr.satis_percentage)) {
        return liveRate * (1 + parseFloat(cr.satis_percentage) / 100);
      }
      if (cr?.rate_satis != null) return parseFloat(cr.rate_satis);
      if (cr?.rate != null) return parseFloat(cr.rate);
      return liveRate;
    };

    const goldRate = calcRate(true);
    const silverRate = calcRate(false);
    const goldInTL = totalGold * goldRate;
    const silverInTL = totalSilver * silverRate;
    const grandTotalTL = totalTL + goldInTL + silverInTL;

    return { totalTL, totalGold, totalSilver, goldInTL, silverInTL, grandTotalTL };
  }, [debtSummary, prices, customRates]);

  // Toplam alacak hesaplama (tüm müşteriler)
  const totalCredits = useMemo(() => {
    const summaryValues = Object.values(creditSummary);
    const totalTL = summaryValues.reduce((sum, d) => sum + (parseFloat(d.total_tl_credit) || 0), 0);
    const totalGold = summaryValues.reduce((sum, d) => sum + (parseFloat(d.total_gold_credit) || 0), 0);
    const totalSilver = summaryValues.reduce((sum, d) => sum + (parseFloat(d.total_silver_credit) || 0), 0);

    const calcRate = (isGold) => {
      const currency = isGold ? 'GOLD' : 'SILVER';
      const cr = customRates[currency];
      const liveRate = isGold
        ? parseFloat(prices?.altin_satis) || 0
        : parseFloat(prices?.gumus_satis) || 0;
      if (cr?.satis_percentage != null && cr.satis_percentage !== '' && !isNaN(cr.satis_percentage)) {
        return liveRate * (1 + parseFloat(cr.satis_percentage) / 100);
      }
      if (cr?.rate_satis != null) return parseFloat(cr.rate_satis);
      if (cr?.rate != null) return parseFloat(cr.rate);
      return liveRate;
    };

    const goldRate = calcRate(true);
    const silverRate = calcRate(false);
    const goldInTL = totalGold * goldRate;
    const silverInTL = totalSilver * silverRate;
    const grandTotalTL = totalTL + goldInTL + silverInTL;

    return { totalTL, totalGold, totalSilver, goldInTL, silverInTL, grandTotalTL };
  }, [creditSummary, prices, customRates]);

  // Borç özetini getir (mağaza tipine göre filtreleme)
  const fetchDebtSummary = async (storeType = 'all') => {
    try {
      const storeTypeParam = storeType && storeType !== 'all' ? `?storeType=${encodeURIComponent(storeType)}` : '';
      const response = await fetch(`${API_URL}/customers/debts/summary${storeTypeParam}`);
      const data = await response.json();
      // customer_id bazlı object'e çevir
      const debtObj = {};
      const creditObj = {};
      data.forEach(d => {
        debtObj[d.customer_id] = d;
        creditObj[d.customer_id] = {
          customer_id: d.customer_id,
          total_tl_credit: parseFloat(d.total_tl_credit) || 0,
          total_gold_credit: parseFloat(d.total_gold_credit) || 0,
          total_silver_credit: parseFloat(d.total_silver_credit) || 0
        };
      });
      setDebtSummary(debtObj);
      setCreditSummary(creditObj);
    } catch (error) {
      console.error('Borç özeti yükleme hatası:', error);
    }
  };

  // Alacak özetini getir (artık fetchDebtSummary içinde birleşik geliyor)
  const fetchCreditSummary = async () => {
    // fetchDebtSummary artık hem borç hem alacak döndürüyor, ayrı çağrıya gerek yok
  };

  // Borç raporu PDF oluştur
  const generateDebtReport = async () => {
    setGeneratingReport(true);
    try {
      const storeTypeParam = storeTypeFilter && storeTypeFilter !== 'all' ? `?storeType=${encodeURIComponent(storeTypeFilter)}` : '';
      const response = await fetch(`${API_URL}/customers/debts/report${storeTypeParam}`);
      const data = await response.json();

      if (!data || data.length === 0) {
        toast.warning('Rapor için borç kaydı bulunamadı');
        setGeneratingReport(false);
        setShowReportModal(false);
        return;
      }

      let totalTL = 0, totalGold = 0, totalSilver = 0;
      data.forEach(d => {
        if (d.debt_type === 'TL') totalTL += parseFloat(d.amount_tl) || 0;
        else if (d.debt_type === 'GRAM') totalGold += parseFloat(d.amount) || 0;
        else if (d.debt_type === 'GUMUS') totalSilver += parseFloat(d.amount) || 0;
      });

      const rows = data.map(d => `
        <tr>
          <td>${d.customer_name || '-'}</td>
          <td>${d.customer_phone || '-'}</td>
          <td>${d.created_at ? new Date(d.created_at).toLocaleDateString('tr-TR') : '-'}</td>
          <td>${d.receipt_number || '-'}</td>
          <td>${d.debt_type === 'TL' ? 'TL' : d.debt_type === 'GRAM' ? 'Altın' : d.debt_type === 'GUMUS' ? 'Gümüş' : d.debt_type}</td>
          <td style="text-align:right">${d.debt_type === 'TL' ? '₺' + (parseFloat(d.amount_tl) || 0).toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2}) : '-'}</td>
          <td style="text-align:right">${d.debt_type !== 'TL' ? (parseFloat(d.amount) || 0).toFixed(2) + ' gr' : '-'}</td>
          <td>${d.description || '-'}</td>
        </tr>
      `).join('');

      const htmlContent = `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align:center; color:#dc2626; margin-bottom:5px;">Borç Raporu</h2>
          <p style="text-align:center; color:#666; font-size:12px; margin-bottom:20px;">Oluşturma: ${new Date().toLocaleString('tr-TR')}</p>
          <table style="width:100%; border-collapse:collapse; font-size:11px;">
            <thead>
              <tr style="background:#f8f9fa; border-bottom:2px solid #dee2e6;">
                <th style="padding:8px 6px; text-align:left;">Müşteri</th>
                <th style="padding:8px 6px; text-align:left;">Telefon</th>
                <th style="padding:8px 6px; text-align:left;">Tarih</th>
                <th style="padding:8px 6px; text-align:left;">Fiş No</th>
                <th style="padding:8px 6px; text-align:left;">Borç Tipi</th>
                <th style="padding:8px 6px; text-align:right;">Tutar (TL)</th>
                <th style="padding:8px 6px; text-align:right;">Tutar (Gram)</th>
                <th style="padding:8px 6px; text-align:left;">Açıklama</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div style="margin-top:15px; padding:12px; background:#fef2f2; border:1px solid #fecaca; border-radius:8px; font-size:12px;">
            <strong>Toplam:</strong>
            ${totalTL > 0 ? `<span style="margin-left:15px;">TL Borç: ₺${totalTL.toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})}</span>` : ''}
            ${totalGold > 0 ? `<span style="margin-left:15px;">Altın Borç: ${totalGold.toFixed(2)} gr</span>` : ''}
            ${totalSilver > 0 ? `<span style="margin-left:15px;">Gümüş Borç: ${totalSilver.toFixed(2)} gr</span>` : ''}
          </div>
        </div>
      `;

      const container = document.createElement('div');
      container.innerHTML = htmlContent;
      document.body.appendChild(container);

      const opt = {
        margin: [10, 10, 10, 10],
        filename: `borc-raporu-${new Date().toISOString().slice(0,10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      html2pdf().set(opt).from(container).save().then(() => {
        document.body.removeChild(container);
        toast.success('Borç raporu PDF indirildi');
      }).catch(() => {
        document.body.removeChild(container);
        toast.error('PDF oluşturulamadı');
      });
    } catch (error) {
      console.error('Borç raporu hatası:', error);
      toast.error('Borç raporu oluşturulamadı');
    }
    setGeneratingReport(false);
    setShowReportModal(false);
  };

  // Alacak raporu PDF oluştur
  const generateCreditReport = async () => {
    setGeneratingReport(true);
    try {
      const storeTypeParam = storeTypeFilter && storeTypeFilter !== 'all' ? `?storeType=${encodeURIComponent(storeTypeFilter)}` : '';
      const response = await fetch(`${API_URL}/customers/credits/report${storeTypeParam}`);
      const data = await response.json();

      if (!data || data.length === 0) {
        toast.warning('Rapor için alacak kaydı bulunamadı');
        setGeneratingReport(false);
        setShowReportModal(false);
        return;
      }

      let totalTL = 0, totalGold = 0, totalSilver = 0;
      data.forEach(d => {
        const unit = d.amount_unit || 'TL';
        if (unit === 'TL') totalTL += parseFloat(d.remaining_amount) || 0;
        else if (unit === 'GRAM') totalGold += parseFloat(d.remaining_amount) || 0;
        else if (unit === 'GUMUS') totalSilver += parseFloat(d.remaining_amount) || 0;
      });

      const formatSource = (src) => {
        if (src === 'scrap_purchase') return 'Hurda Alımı';
        if (src === 'manual') return 'Manuel';
        return src || '-';
      };

      const formatUnit = (unit) => {
        if (unit === 'TL') return 'TL';
        if (unit === 'GRAM') return 'Altın';
        if (unit === 'GUMUS') return 'Gümüş';
        return unit || 'TL';
      };

      const rows = data.map(d => `
        <tr>
          <td>${d.customer_name || '-'}</td>
          <td>${d.customer_phone || '-'}</td>
          <td>${d.created_at ? new Date(d.created_at).toLocaleDateString('tr-TR') : '-'}</td>
          <td>${d.code || '-'}</td>
          <td>${formatSource(d.source)}</td>
          <td>${formatUnit(d.amount_unit)}</td>
          <td style="text-align:right">${(d.amount_unit === 'TL' || !d.amount_unit) ? '₺' + (parseFloat(d.remaining_amount) || 0).toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2}) : '-'}</td>
          <td style="text-align:right">${(d.amount_unit === 'GRAM' || d.amount_unit === 'GUMUS') ? (parseFloat(d.remaining_amount) || 0).toFixed(2) + ' gr' : '-'}</td>
          <td>${d.notes || '-'}</td>
        </tr>
      `).join('');

      const htmlContent = `
        <div style="font-family: Arial, sans-serif; padding: 20px;">
          <h2 style="text-align:center; color:#16a34a; margin-bottom:5px;">Alacak Raporu</h2>
          <p style="text-align:center; color:#666; font-size:12px; margin-bottom:20px;">Oluşturma: ${new Date().toLocaleString('tr-TR')}</p>
          <table style="width:100%; border-collapse:collapse; font-size:11px;">
            <thead>
              <tr style="background:#f8f9fa; border-bottom:2px solid #dee2e6;">
                <th style="padding:8px 6px; text-align:left;">Müşteri</th>
                <th style="padding:8px 6px; text-align:left;">Telefon</th>
                <th style="padding:8px 6px; text-align:left;">Tarih</th>
                <th style="padding:8px 6px; text-align:left;">Kod</th>
                <th style="padding:8px 6px; text-align:left;">Kaynak</th>
                <th style="padding:8px 6px; text-align:left;">Alacak Tipi</th>
                <th style="padding:8px 6px; text-align:right;">Tutar (TL)</th>
                <th style="padding:8px 6px; text-align:right;">Tutar (Gram)</th>
                <th style="padding:8px 6px; text-align:left;">Açıklama</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div style="margin-top:15px; padding:12px; background:#ecfdf5; border:1px solid #a7f3d0; border-radius:8px; font-size:12px;">
            <strong>Toplam:</strong>
            ${totalTL > 0 ? `<span style="margin-left:15px;">TL Alacak: ₺${totalTL.toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})}</span>` : ''}
            ${totalGold > 0 ? `<span style="margin-left:15px;">Altın Alacak: ${totalGold.toFixed(2)} gr</span>` : ''}
            ${totalSilver > 0 ? `<span style="margin-left:15px;">Gümüş Alacak: ${totalSilver.toFixed(2)} gr</span>` : ''}
          </div>
        </div>
      `;

      const container = document.createElement('div');
      container.innerHTML = htmlContent;
      document.body.appendChild(container);

      const opt = {
        margin: [10, 10, 10, 10],
        filename: `alacak-raporu-${new Date().toISOString().slice(0,10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      html2pdf().set(opt).from(container).save().then(() => {
        document.body.removeChild(container);
        toast.success('Alacak raporu PDF indirildi');
      }).catch(() => {
        document.body.removeChild(container);
        toast.error('PDF oluşturulamadı');
      });
    } catch (error) {
      console.error('Alacak raporu hatası:', error);
      toast.error('Alacak raporu oluşturulamadı');
    }
    setGeneratingReport(false);
    setShowReportModal(false);
  };

  // Altın/Gümüş müşteri sayılarını getir
  const fetchStoreTypeCounts = async () => {
    try {
      const response = await fetch(`${API_URL}/customers/store-type-counts`);
      const data = await response.json();
      setGoldCustomersCount(data.gold || 0);
      setSilverCustomersCount(data.silver || 0);
    } catch (error) {
      console.error('Mağaza tipi sayıları yükleme hatası:', error);
    }
  };

  // Anlık fiyatları getir
  const fetchPrices = async () => {
    try {
      const response = await fetch(`${API_URL}/prices`);
      const data = await response.json();
      setPrices(data);
    } catch (error) {
      console.error('Fiyat yükleme hatası:', error);
    }
  };

  // Makaslı kur ile fiyat hesapla (debt_type'a göre)
  const getEffectiveRate = (debtType) => {
    const isGold = debtType === 'GRAM';
    const currency = isGold ? 'GOLD' : 'SILVER';
    const customRate = customRates[currency];

    // Anlık piyasa fiyatı
    const liveRate = isGold
      ? parseFloat(prices?.altin_satis) || 0
      : parseFloat(prices?.gumus_satis) || 0;

    // Önce yüzdeye bak (makas farkı), sonra sabit kura bak
    if (customRate?.satis_percentage != null && customRate.satis_percentage !== '' && !isNaN(customRate.satis_percentage)) {
      return liveRate * (1 + parseFloat(customRate.satis_percentage) / 100);
    }
    if (customRate?.rate_satis != null) {
      return parseFloat(customRate.rate_satis);
    }
    if (customRate?.rate != null) {
      return parseFloat(customRate.rate);
    }

    return liveRate;
  };

  // Güncel TL değerini hesapla (makaslı kur ile)
  const calculateCurrentValue = (debt) => {
    if (debt.debt_type === 'TL') {
      return Number(debt.amount_tl);
    }

    const gramAmount = Number(debt.amount);

    if (debt.debt_type === 'GRAM') {
      const effectiveGoldPrice = getEffectiveRate('GRAM');
      return effectiveGoldPrice > 0 ? gramAmount * effectiveGoldPrice : Number(debt.amount_tl);
    }

    if (debt.debt_type === 'GUMUS') {
      const effectiveSilverPrice = getEffectiveRate('GUMUS');
      return effectiveSilverPrice > 0 ? gramAmount * effectiveSilverPrice : Number(debt.amount_tl);
    }

    return Number(debt.amount_tl);
  };

  // Mağaza türü kontrolü
  const currentStoreId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
  const isGoldStore = currentStoreId === 1;

  // Hurda alış fiyatı (tahsilat için)
  const getScrapAlisRate = () => {
    const currency = isGoldStore ? 'GOLD' : 'SILVER';
    const customRate = customRates[currency];
    const liveAlis = isGoldStore
      ? parseFloat(prices?.altin_alis) || 0
      : parseFloat(prices?.gumus_alis) || 0;

    // Önce yüzdeye bak, sonra sabit kura
    if (customRate?.alis_percentage != null && customRate.alis_percentage !== '' && !isNaN(customRate.alis_percentage)) {
      return liveAlis * (1 - parseFloat(customRate.alis_percentage) / 100);
    }
    if (customRate?.rate_alis != null) {
      return parseFloat(customRate.rate_alis);
    }
    return liveAlis;
  };

  // Hurda tutarı hesapla
  const calculateScrapPaymentValue = () => {
    const grossWeight = parseFloat(scrapPaymentData.gross_weight) || 0;
    const wasteGram = parseFloat(scrapPaymentData.waste_gram) || 0;
    const unitPrice = parseFloat(scrapPaymentData.unit_price) || 0;
    const purity = scrapPaymentData.purity;

    if (grossWeight <= 0 || unitPrice <= 0) return { pureWeight: 0, totalValue: 0 };

    // Fire hesaplama (sadece gram)
    const netWeight = grossWeight - wasteGram;

    // Ayara göre has hesaplama - önce custom_purity_rate'i kontrol et, yoksa varsayılanı kullan
    const metalType = isGoldStore ? 'gold' : 'silver';
    const purityFactor = scrapPaymentData.custom_purity_rate || scrapPurityRates[metalType]?.[purity] || defaultPurityRates[metalType]?.[purity] || (isGoldStore ? 0.900 : 0.925);

    const pureWeight = netWeight * purityFactor;
    // Manuel değer varsa onu kullan
    const totalValue = customScrapTotal !== null ? customScrapTotal : pureWeight * unitPrice;

    return { pureWeight, totalValue, netWeight, wasteGram };
  };

  // Banka hesaplarını getir
  const fetchBankAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/bank-accounts?active_only=true`);
      const data = await response.json();
      if (data.success) {
        setBankAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Banka hesapları yüklenirken hata:', error);
    }
  };

  // Borç raporu yazdır
  const printDebtReport = (customer) => {
    const debt = debtSummary[customer.id];
    const credit = creditSummary[customer.id];
    const tlDebt = debt ? Number(debt.total_tl_debt) : 0;
    const tlCredit = credit ? Number(credit.total_tl_credit) : 0;
    const goldDebt = debt ? Number(debt.total_gold_debt || 0) : 0;
    const silverDebt = debt ? Number(debt.total_silver_debt || 0) : 0;
    const netBalance = tlDebt - tlCredit;

    const now = new Date();
    const dateStr = now.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

    const storeId = currentStoreId;

    const debtHtml = `<!DOCTYPE html>
<html>
<head>
  <title>Bakiye Raporu - ${customer.name}</title>
  <style>
    @page { size: 80mm auto; margin: 5mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 12px;
      font-weight: 600;
      width: 80mm;
      padding: 5mm;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .receipt-top-space { height: 8mm; }
    /* Fişin altına boşluk. Termal yazıcı 1-bit basar: açık gri (#f2f2f2) beyaza dönüyor ve sürücü
       sondaki "boş" alanı kağıt tasarrufu için kesiyordu. Bu yüzden boşluğun EN ALTINA gerçekten
       siyah küçük bir işaret koyuyoruz; yazıcı ona kadar basmak zorunda, aradaki boşluk korunur. */
    .receipt-bottom-space { height: 15mm; }
    .receipt-end-mark { text-align: center; font-size: 10px; color: #000; line-height: 1; }
    .header {
      text-align: center;
      border-bottom: 2px dashed #000;
      padding-bottom: 10px;
      margin-bottom: 10px;
    }
    .logo-img {
      width: 120px;
      height: auto;
      margin-bottom: 8px;
      filter: invert(1);
    }
    .receipt-title {
      font-size: 14px;
      font-weight: bold;
      margin-top: 8px;
      padding: 4px 0;
      border-top: 1px solid #000;
    }
    .divider {
      border-top: 1px dashed #333;
      margin: 8px 0;
    }
    .row {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      font-size: 11px;
    }
    .row.bold {
      font-weight: bold;
      font-size: 12px;
    }
    .row-grand-total {
      display: flex;
      justify-content: space-between;
      font-size: 15px;
      font-weight: bold;
      padding: 8px 0;
      border-top: 2px solid #000;
      border-bottom: 2px solid #000;
      margin: 8px 0;
    }
    .section-label {
      font-size: 12px;
      font-weight: bold;
      margin-bottom: 4px;
      padding-bottom: 2px;
      border-bottom: 1px solid #333;
    }
    .footer {
      text-align: center;
      margin-top: 15px;
      padding-top: 10px;
      border-top: 2px dashed #000;
      font-size: 10px;
      color: #000;
    }
    @media print {
      body { width: 80mm; }
    }
  </style>
</head>
<body>
  <div class="receipt-top-space"></div>
  <div class="header">
    ${storeId === 1
      ? `<img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" class="logo-img" />`
      : `<div style="margin: 0 auto 2px auto; text-align: center;">
          <img src="/akikfisligi.png" alt="Akik Gümüş" style="width: 170px; height: auto; filter: contrast(3);" />
        </div>`
    }
    ${storeId === 1
      ? `<div style="font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 2px;">
          Osmangazi Mah. Ahmet Şireci Bulvarı No: 14D<br/>
          Şehitkamil / Gaziantep<br/>
          Tel: 0342 230 00 64<br/>
          www.akikjewellery.com
        </div>`
      : `<div style="font-size: 11px; font-weight: bold; color: #000; margin-bottom: 3px;">
          ${storeId === 3 ? '(YEŞİLSU ŞUBESİ)' : '(OSMANGAZİ ŞUBESİ)'}
        </div>
        <div style="font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 2px;">
          ${storeId === 3
            ? 'Bey Mah. Atatürk Blv. 43/A<br/>Şahinbey / Gaziantep'
            : 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 16/F<br/>Şehitkamil / Gaziantep'}
          <br/>Tel: 0342 230 00 64
        </div>`
    }
    <div class="receipt-title">BAKİYE RAPORU</div>
  </div>

  <div class="section-label">Müşteri Bilgileri</div>
  <div class="row"><span>Müşteri:</span><span>${customer.name || '-'}</span></div>
  <div class="row"><span>Telefon:</span><span>${customer.phone || '-'}</span></div>
  <div class="row"><span>Tarih:</span><span>${dateStr} ${timeStr}</span></div>

  <div class="divider"></div>

  ${netBalance !== 0 ? `<div class="row-grand-total">
    <span>Bakiye:</span>
    <span>₺${netBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
  </div>` : ''}
  ${goldDebt > 0 ? `<div class="row-grand-total">
    <span>Altın Borcu:</span>
    <span>${goldDebt.toFixed(2)} gr</span>
  </div>` : ''}
  ${silverDebt > 0 ? `<div class="row-grand-total">
    <span>Gümüş Borcu:</span>
    <span>${silverDebt.toFixed(2)} gr</span>
  </div>` : ''}

  <div class="receipt-bottom-space"></div>
  <div class="receipt-end-mark">.</div>

  <script>
    window.onload = function() {
      var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
      setTimeout(function() {
        window.print();
        if (directPrint) { setTimeout(function() { window.close(); }, 500); }
      }, 300);
    }
  </script>
</body>
</html>`;
    handlePrint(debtHtml, 'receipt');
  };

  // Borç listesini netleştir: iade/tahsilat edilmişleri hariç tut
  const filterActiveDebts = (allDebtsArr) => {
    const arr = Array.isArray(allDebtsArr) ? allDebtsArr : [];
    // TL: sale_id bazında netleştir (satış+iade eşleştirmesi)
    const tlEntries = arr.filter(d => (d.debt_type || d.currency_type || 'TL') === 'TL');

    // sale_id'li kayıtları grupla
    const saleGroups = {};
    // sale_id'siz pozitif kayıtlar (manuel borçlar, değişim farkları vb.) ayrı tut
    const nonSaleDebts = [];
    // sale_id'siz negatif kayıtlar (tahsilatlar) toplamı
    let nonSaleCollections = 0;

    tlEntries.forEach(d => {
      const amt = parseFloat(d.amount_tl) || 0;
      if (d.sale_id) {
        const key = `sale_${d.sale_id}`;
        if (!saleGroups[key]) saleGroups[key] = { baseEntry: d, netTl: 0 };
        saleGroups[key].netTl += amt;
        if (amt > 0 && d.transaction_type === 'sale') saleGroups[key].baseEntry = d;
      } else {
        if (amt > 0) {
          nonSaleDebts.push({ entry: d, remaining: amt });
        } else {
          nonSaleCollections += amt; // negatif değer
        }
      }
    });

    // Tüm müşteri alacaklarını (sale_id'siz tahsilatlar + sale_id'li iade fazlaları) topla.
    // Bir satışa bağlı iade, o satışın açık hesap kısmından fazlaysa (örn. 13500 açık hesap
    // satışına 18000 iade), geriye kalan -4500 kredi diğer borçları da azaltmalı; aksi halde
    // tahsilat modalında yanlış (yüksek) tutar gösterilir.
    let surplus = Math.abs(nonSaleCollections);
    for (const key of Object.keys(saleGroups)) {
      if (saleGroups[key].netTl < -0.01) {
        surplus += Math.abs(saleGroups[key].netTl);
        saleGroups[key].netTl = 0;
      }
    }
    if (surplus > 0.01) {
      const saleKeys = Object.keys(saleGroups)
        .filter(k => saleGroups[k].netTl > 0.01)
        .sort((a, b) => {
          const dateA = new Date(saleGroups[a].baseEntry.created_at || 0);
          const dateB = new Date(saleGroups[b].baseEntry.created_at || 0);
          return dateA - dateB;
        });
      for (const key of saleKeys) {
        if (surplus <= 0.01) break;
        const deduct = Math.min(surplus, saleGroups[key].netTl);
        saleGroups[key].netTl -= deduct;
        surplus -= deduct;
      }
      // Kalan fazlalık varsa sale_id'siz borçlardan düş (eskiden yeniye)
      if (surplus > 0.01) {
        const sortedNonSale = [...nonSaleDebts].sort((a, b) =>
          new Date(a.entry.created_at || 0) - new Date(b.entry.created_at || 0)
        );
        for (const nsd of sortedNonSale) {
          if (surplus <= 0.01) break;
          const deduct = Math.min(surplus, nsd.remaining);
          nsd.remaining -= deduct;
          surplus -= deduct;
        }
      }
    }

    // Aktif TL borçları oluştur
    const activeTl = [];
    // Satış gruplarından kalan borçlar
    Object.values(saleGroups)
      .filter(g => g.netTl > 0.01)
      .forEach(g => activeTl.push({ ...g.baseEntry, amount_tl: g.netTl, amount: g.netTl }));
    // sale_id'siz kalan borçlar (manuel borçlar, değişim farkları)
    nonSaleDebts
      .filter(nsd => nsd.remaining > 0.01)
      .forEach(nsd => activeTl.push({ ...nsd.entry, amount_tl: nsd.remaining, amount: nsd.remaining }));

    // GRAM/GUMUS: para birimi bazında TOPLAM net hesapla
    // (tahsilat kayıtlarının sale_id'si olmadığı için sale_id bazlı netleştirme çalışmaz)
    const metalTypes = ['GRAM', 'GUMUS'];
    const activeMetal = [];
    for (const metalType of metalTypes) {
      const entries = arr.filter(d => (d.debt_type || d.currency_type) === metalType);
      if (entries.length === 0) continue;
      const netGram = entries.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
      const netTl = entries.reduce((sum, d) => sum + (parseFloat(d.amount_tl) || 0), 0);
      if (netGram > 0.0001) {
        // En son pozitif (satış) kaydını temel kayıt olarak kullan
        const baseEntry = entries.find(d => parseFloat(d.amount) > 0 && d.transaction_type === 'sale') || entries[0];
        activeMetal.push({ ...baseEntry, amount: netGram, amount_tl: netTl });
      }
    }
    return [...activeTl, ...activeMetal];
  };

  // Müşterinin borçlarını getir
  const fetchCustomerDebts = async (customer) => {
    setSelectedCustomer(customer);
    setShowDebtModal(true);
    setLoadingDebts(true);

    try {
      // Hem borçları hem de güncel fiyatları paralel getir
      const [debtsResponse, pricesResponse] = await Promise.all([
        fetch(`${API_URL}/customers/${customer.id}/debts`),
        fetch(`${API_URL}/prices`)
      ]);
      const debtsData = await debtsResponse.json();
      const pricesData = await pricesResponse.json();
      // Aktif borçları netleştirerek göster
      const allDebts = debtsData.success ? debtsData.debts : debtsData;
      setCustomerDebts(filterActiveDebts(allDebts));
      // TL Bakiye geçmişi: sadece TL transaction'lar (altın/gümüş bağımsız)
      const allTx = debtsData.transactions || [];
      const tlTransactions = allTx.filter(tx => (tx.debt_type || tx.currency_type || 'TL') === 'TL');
      const sortedTx = [...tlTransactions].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      // Her işlem için önceki ve sonraki bakiyeyi hesapla
      let runningBalance = 0;
      const historyWithBalance = sortedTx.map(tx => {
        const prevBalance = runningBalance;
        runningBalance += parseFloat(tx.amount_tl) || 0;
        return { ...tx, prev_balance: prevBalance, new_balance: runningBalance };
      });
      setBalanceHistory(historyWithBalance);
      // TL bakiye (altın/gümüş hariç)
      setCustomerBalance(debtsData.tl_balance ?? debtsData.balance ?? 0);

      // Gram/Gümüş bakiye geçmişi
      const gramGumusTransactions = allTx.filter(tx => {
        const t = tx.debt_type || tx.currency_type || 'TL';
        return t === 'GRAM' || t === 'GUMUS';
      });
      const sortedGramTx = [...gramGumusTransactions].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      let runningGram = 0;
      let runningGumus = 0;
      const gramHistoryWithBalance = sortedGramTx.map(tx => {
        const t = tx.debt_type || tx.currency_type;
        const amt = parseFloat(tx.amount) || 0;
        const prevGram = runningGram;
        const prevGumus = runningGumus;
        if (t === 'GRAM') runningGram += amt;
        else if (t === 'GUMUS') runningGumus += amt;
        return { ...tx, prev_gram: prevGram, prev_gumus: prevGumus, new_gram: runningGram, new_gumus: runningGumus };
      });
      setGramBalanceHistory(gramHistoryWithBalance);
      setGramBalance(debtsData.gram_balance ?? 0);
      setGumusBalance(debtsData.gumus_balance ?? 0);

      setPrices(pricesData);
    } catch (error) {
      console.error('Borç yükleme hatası:', error);
      setCustomerDebts([]);
    } finally {
      setLoadingDebts(false);
    }
  };

  // Ödeme modalını aç
  const openPaymentModal = (debt) => {
    // Banka hesaplarını yükle (Havale/EFT için)
    fetchBankAccounts();

    // Net bakiyeyi kullan (bireysel işlem tutarı yerine grup net bakiyesi)
    // Grup bilgisini ekle ki ödeme doğru endpoint'e gitsin
    const debtType = debt.debt_type || 'TL';
    const group = groupedCustomerDebts.find(g => g.debt_type === debtType);
    const adjustedDebt = group ? {
      ...debt,
      amount: group.total_amount,
      amount_tl: group.total_amount_tl,
      debt_ids: group.debt_ids,
      is_group: true
    } : debt;

    setSelectedDebt(adjustedDebt);
    setPaymentAmount('');
    setPaymentMethod('');
    setMixedPayments([]); // Karışık ödemeleri sıfırla
    setSelectedBankAccount(null); // Seçili banka hesabını sıfırla
    setPaymentInputMode('tl'); // Giriş modunu TL olarak sıfırla
    // Hurda modunu sıfırla
    setScrapPaymentMode(false);
    setScrapPaymentData({
      gross_weight: '',
      waste_percent: '',
      waste_gram: '',
      purity: isGoldStore ? '22' : '925',
      unit_price: getScrapAlisRate().toFixed(2)
    });
    setShowPaymentModal(true);
  };

  // Grup ödeme modalını aç (tüm borçları toplu öde)
  const openGroupPaymentModal = (group) => {
    // Banka hesaplarını yükle (Havale/EFT için)
    fetchBankAccounts();

    // Grup için sanal bir borç objesi oluştur
    const virtualDebt = {
      id: 'group_' + group.debt_type,
      debt_type: group.debt_type,
      amount: group.total_amount,
      amount_tl: group.total_amount_tl,
      debt_ids: group.debt_ids, // Grup içindeki tüm borç ID'leri
      is_group: true
    };
    setSelectedDebt(virtualDebt);

    setPaymentAmount('');
    setPaymentMethod('');
    setMixedPayments([]); // Karışık ödemeleri sıfırla
    setSelectedBankAccount(null); // Seçili banka hesabını sıfırla
    setPaymentInputMode('tl'); // Giriş modunu TL olarak sıfırla
    // Hurda modunu sıfırla
    setScrapPaymentMode(false);
    setScrapPaymentData({
      gross_weight: '',
      waste_percent: '',
      waste_gram: '',
      purity: isGoldStore ? '22' : '925',
      unit_price: getScrapAlisRate().toFixed(2)
    });
    setShowPaymentModal(true);
  };

  // Makaslı kur hesapla (modal için)
  const calculateMarginRate = () => {
    if (!selectedDebt || (selectedDebt.debt_type !== 'GRAM' && selectedDebt.debt_type !== 'GUMUS')) return 0;
    return getEffectiveRate(selectedDebt.debt_type);
  };

  // Makas yüzdesini al (gösterim için)
  const getMarginPercentage = () => {
    if (!selectedDebt || (selectedDebt.debt_type !== 'GRAM' && selectedDebt.debt_type !== 'GUMUS')) return null;

    const currency = selectedDebt.debt_type === 'GRAM' ? 'GOLD' : 'SILVER';
    const customRate = customRates[currency];

    if (customRate?.satis_percentage != null && customRate.satis_percentage !== '' && !isNaN(customRate.satis_percentage)) {
      return parseFloat(customRate.satis_percentage);
    }
    return null;
  };

  // Ödeme tutarının gram karşılığını hesapla
  const calculateGramEquivalent = () => {
    if (!selectedDebt || (selectedDebt.debt_type !== 'GRAM' && selectedDebt.debt_type !== 'GUMUS')) return 0;

    const rate = calculateMarginRate();
    if (rate <= 0) return 0;

    const amount = parseFloat(paymentAmount) || 0;
    return amount / rate;
  };

  // Karışık ödeme - Ödeme ekle
  const addPayment = (methodOverride) => {
    const method = methodOverride || paymentMethod;
    if (!method && !scrapPaymentMode) {
      toast.warning('Lütfen önce ödeme yöntemi seçin!');
      return;
    }
    const inputValue = parseFloat(paymentAmount);
    if (isNaN(inputValue) || inputValue <= 0) {
      toast.warning('Geçerli bir tutar girin!');
      return;
    }

    // Gram modu ve altın/gümüş borcu için TL'ye çevir
    let tlAmount = inputValue;
    let gramAmount = null;
    const isGramDebt = selectedDebt?.debt_type === 'GRAM' || selectedDebt?.debt_type === 'GUMUS';

    if (paymentInputMode === 'gram' && isGramDebt) {
      const rate = calculateMarginRate();
      if (rate <= 0) {
        toast.warning('Kur bilgisi alınamadı!');
        return;
      }
      tlAmount = inputValue * rate;
      gramAmount = inputValue;
    }

    // Borç tutarını hesapla
    const maxAmount = calculateCurrentValue(selectedDebt);
    const currentTotal = mixedPayments.reduce((sum, p) => sum + p.amount, 0);
    const remaining = maxAmount - currentTotal;

    // Altın/gümüş borçlarda kurun belirsizliği nedeniyle bakiyeden fazla tahsilata izin verilmez.
    // TL borçta müşteri isterse fazla ödeyebilir - fazlası müşteri alacağı olarak bakiyede kalır.
    if (isGramDebt && tlAmount > remaining * 1.01) { // %1 tolerans
      toast.warning(`Kalan borç tutarı: ₺${remaining.toLocaleString('tr-TR', {maximumFractionDigits: 2})}`);
      return;
    }
    if (!isGramDebt && tlAmount > remaining * 1.01 && remaining > 0.01) {
      toast.info(`Kalan borç ₺${remaining.toLocaleString('tr-TR', {maximumFractionDigits: 2})} - fazlası (₺${(tlAmount - remaining).toLocaleString('tr-TR', {maximumFractionDigits: 2})}) müşteri alacağı olarak kaydedilecek.`);
    }

    // Havale/EFT seçiliyse banka hesabı seçili olmalı
    if (method === 'Havale/EFT') {
      if (!selectedBankAccount) {
        toast.warning('Lütfen bir banka hesabı seçin!');
        return;
      }
      const accountType = selectedBankAccount.account_type === 'company' ? 'Şirket' : 'Şahsi';
      const newPayment = {
        id: Date.now(),
        method: 'Havale/EFT',
        amount: tlAmount,
        gramAmount: gramAmount,
        debtType: selectedDebt?.debt_type,
        bankAccount: `${selectedBankAccount.account_name} (${accountType})`,
        bankAccountId: selectedBankAccount.id
      };
      setMixedPayments(prev => [...prev, newPayment]);
      setPaymentAmount('');
      setPaymentMethod('');
      setSelectedBankAccount(null);
      if (gramAmount) {
        toast.success(`${gramAmount.toFixed(selectedDebt?.debt_type === 'GUMUS' ? 2 : 3)} gr (₺${tlAmount.toLocaleString('tr-TR', {minimumFractionDigits: 2})}) Havale/EFT - ${selectedBankAccount.account_name} eklendi`);
      } else {
        toast.success(`₺${tlAmount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} Havale/EFT - ${selectedBankAccount.account_name} eklendi`);
      }
      return;
    }

    const newPayment = {
      id: Date.now(),
      method: method,
      amount: tlAmount,
      gramAmount: gramAmount,
      debtType: selectedDebt?.debt_type
    };

    setMixedPayments(prev => [...prev, newPayment]);
    setPaymentAmount('');
    setPaymentMethod('');
    setScrapPaymentMode(false);

    if (gramAmount) {
      toast.success(`${gramAmount.toFixed(selectedDebt?.debt_type === 'GUMUS' ? 2 : 3)} gr (₺${tlAmount.toLocaleString('tr-TR', {minimumFractionDigits: 2})}) ${method} eklendi`);
    } else {
      toast.success(`₺${tlAmount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${method} eklendi`);
    }
  };

  // Karışık ödeme - Ödeme kaldır
  const removePayment = (paymentId) => {
    setMixedPayments(prev => prev.filter(p => p.id !== paymentId));
  };

  // Karışık ödeme - Toplam tutarı al
  const getPaymentsTotal = () => {
    return mixedPayments.reduce((sum, p) => sum + p.amount, 0);
  };

  // Karışık ödeme - Kalan borç
  const getRemainingDebt = () => {
    const maxAmount = selectedDebt ? calculateCurrentValue(selectedDebt) : 0;
    return maxAmount - getPaymentsTotal();
  };

  // Satış sorumlularını getir
  const fetchSalesStaff = async () => {
    try {
      const response = await fetch(`${API_URL}/sales-staff`);
      const data = await response.json();
      setSalesStaffList(data);
    } catch (error) {
      console.error('Satış sorumlusu yükleme hatası:', error);
    }
  };

  // Ödeme modalı açıldığında satış sorumlularını yükle
  useEffect(() => {
    if (showPaymentModal) {
      fetchSalesStaff();
    }
  }, [showPaymentModal]);

  // Borcu ödendi olarak işaretle (kısmi ödeme destekli)
  const markDebtAsPaid = async () => {
    if (!selectedDebt) return;

    let amount;
    let scrapPurchaseId = null;
    let paymentMethodText = '';
    let paymentsToProcess = [];

    // Hurda ile ödeme
    if (scrapPaymentMode) {
      const scrapCalc = calculateScrapPaymentValue();
      if (scrapCalc.totalValue <= 0) {
        toast.warning('Lütfen hurda bilgilerini girin!');
        return;
      }
      amount = scrapCalc.totalValue;
      paymentMethodText = `Hurda (${isGoldStore ? 'Altın' : 'Gümüş'})`;

      // Önce hurda alımını kaydet
      try {
        const scrapResponse = await fetch(`${API_URL}/scrap-purchases`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_id: selectedCustomer?.id || null,
            customer_name: selectedCustomer?.name || 'Tahsilat Müşterisi',
            customer_phone: selectedCustomer?.phone || '',
            staff_id: collectionStaffId || user?.id || null,
            staff_name: collectionStaffName || user?.name || null,
            store_id: currentStoreId,
            metal_type: isGoldStore ? 'gold' : 'silver',
            purity: scrapPaymentData.purity,
            gross_weight: parseFloat(scrapPaymentData.gross_weight),
            waste_percent: parseFloat(scrapPaymentData.waste_percent) || 0,
            waste_gram: parseFloat(scrapPaymentData.waste_gram) || 0,
            pure_weight: scrapCalc.pureWeight,
            unit_price: parseFloat(scrapPaymentData.unit_price),
            total_amount: scrapCalc.totalValue,
            notes: `Tahsilat - Borç ID: ${selectedDebt.id}`,
            payment_method: 'Tahsilat Mahsup',
            paid_cash: 0,
            paid_transfer: 0,
            paid_credit: scrapCalc.totalValue
          })
        });
        const scrapData = await scrapResponse.json();
        if (scrapData.success) {
          scrapPurchaseId = scrapData.id;
          // Log kaydet
          logService.log('scrap', 'create', `Tahsilat hurda alımı: ${scrapCalc.pureWeight}gr ${isGoldStore ? 'altın' : 'gümüş'} - ${scrapCalc.totalValue} TL`, {
            entity_type: 'scrap',
            entity_id: scrapData.id,
            status: 'success',
            details: {
              customer_name: selectedCustomer?.name,
              metal_type: isGoldStore ? 'gold' : 'silver',
              pure_weight: scrapCalc.pureWeight,
              total_value: scrapCalc.totalValue,
              debt_id: selectedDebt.id
            }
          });
        } else {
          toast.error('Hurda kaydedilemedi!');
          return;
        }
      } catch (error) {
        console.error('Hurda kaydetme hatası:', error);
        toast.error('Hurda kaydedilemedi!');
        return;
      }
    } else if (mixedPayments.length > 0) {
      // Karışık ödeme modu
      amount = getPaymentsTotal();
      paymentsToProcess = [...mixedPayments];

      // Ödeme yöntemlerini birleştir
      const methodCounts = {};
      paymentsToProcess.forEach(p => {
        methodCounts[p.method] = (methodCounts[p.method] || 0) + p.amount;
      });
      paymentMethodText = Object.entries(methodCounts)
        .map(([method, amt]) => `${method}: ₺${amt.toLocaleString('tr-TR', {minimumFractionDigits: 0})}`)
        .join(', ');

      if (amount <= 0) {
        toast.warning('Lütfen en az bir ödeme ekleyin!');
        return;
      }
    } else {
      amount = parseFloat(paymentAmount);
      if (isNaN(amount) || amount <= 0) {
        toast.warning('Geçerli bir tutar girin!');
        return;
      }
      paymentMethodText = paymentMethod;
    }

    // Gram borçlar için gram miktarını kontrol et
    const isGramDebt = selectedDebt.debt_type === 'GRAM' || selectedDebt.debt_type === 'GUMUS';
    const gramAmount = isGramDebt ? calculateGramEquivalent() : 0;
    const maxGram = parseFloat(selectedDebt.amount) || 0;

    if (isGramDebt && !scrapPaymentMode && gramAmount > maxGram) {
      toast.warning(`Düşülecek gram (${gramAmount.toFixed(3)} gr) borç gramından (${maxGram.toFixed(3)} gr) fazla olamaz!`);
      return;
    }

    try {
      // Karışık ödeme için ödeme yöntemini belirle
      const finalPaymentMethod = mixedPayments.length > 1 ? 'Karışık' : paymentMethodText;

      // Grup ödeme mi yoksa tek borç ödemesi mi?
      let response;
      if (selectedDebt.is_group && selectedDebt.debt_ids && selectedDebt.debt_ids.length > 0) {
        // Grup ödeme - tüm borçları bir seferde öde
        response = await fetch(`${API_URL}/debts/group/pay`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            debtIds: selectedDebt.debt_ids,
            customerId: selectedCustomer?.id,
            paymentMethod: finalPaymentMethod,
            paymentAmount: amount,
            gramAmount: isGramDebt && !scrapPaymentMode && mixedPayments.length === 0 ? gramAmount : null,
            marginRate: isGramDebt && !scrapPaymentMode && mixedPayments.length === 0 ? calculateMarginRate() : null,
            scrapPurchaseId: scrapPurchaseId,
            storeId: currentStoreId,
            staffId: collectionStaffId || user?.id,
            staffName: collectionStaffName || user?.name,
            paymentDetails: paymentsToProcess.length > 0 ? paymentsToProcess : null
          })
        });
      } else {
        // Tek borç ödemesi
        response = await fetch(`${API_URL}/debts/${selectedDebt.id}/pay`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: selectedCustomer?.id,
            paymentMethod: finalPaymentMethod,
            paymentAmount: amount,
            gramAmount: isGramDebt && !scrapPaymentMode && mixedPayments.length === 0 ? gramAmount : null,
            marginRate: isGramDebt && !scrapPaymentMode && mixedPayments.length === 0 ? calculateMarginRate() : null,
            scrapPurchaseId: scrapPurchaseId,
            storeId: currentStoreId,
            staffId: collectionStaffId || user?.id,
            staffName: collectionStaffName || user?.name,
            paymentDetails: paymentsToProcess.length > 0 ? paymentsToProcess : null
          })
        });
      }
      const data = await response.json();
      if (!data.success && !response.ok) {
        toast.error(data.error || 'Tahsilat işlemi başarısız!');
        logService.log('customers', 'debt_payment', `Borç tahsilat hatası: ${selectedCustomer?.name} - ${data.error}`, {
          status: 'error',
          details: { error: data.error, debtIds: selectedDebt.debt_ids, debtId: selectedDebt.id }
        });
        return;
      }
      if (data.success) {
        // Ödeme log'u
        const debtTypeTurkish = selectedDebt.debt_type === 'GRAM' ? 'Gram Altın' : selectedDebt.debt_type === 'GUMUS' ? 'Gram Gümüş' : 'TL';
        logService.log('customers', 'debt_payment',
          `Borç tahsilatı: ${selectedCustomer?.name} - ₺${amount.toLocaleString()} (${paymentMethodText})`, {
          entity_type: 'customer',
          entity_id: selectedCustomer?.id,
          details: {
            musteri: selectedCustomer?.name,
            borc_turu: debtTypeTurkish,
            borc_tutari: (selectedDebt.debt_type === 'GRAM' || selectedDebt.debt_type === 'GUMUS')
              ? `${Number(selectedDebt.amount).toFixed(selectedDebt.debt_type === 'GUMUS' ? 2 : 3)} gr (₺${Number(selectedDebt.amount_tl).toLocaleString()})`
              : `₺${Number(selectedDebt.amount_tl).toLocaleString()}`,
            odenen_tutar: `₺${amount.toLocaleString()}`,
            kalan_borc: data.isFullyPaid ? '₺0' : `₺${data.remaining?.toLocaleString()}`,
            odeme_yontemi: paymentMethodText
          },
          status: 'success'
        });

        // Borç listesini sunucudan yeniden çek (tutarlılık için)
        const debtsResponse = await fetch(`${API_URL}/customers/${selectedCustomer.id}/debts`);
        const updatedDebts = await debtsResponse.json();
        const allDebts = updatedDebts.success ? updatedDebts.debts : updatedDebts;
        setCustomerDebts(filterActiveDebts(allDebts));

        // Mesaj göster
        if (data.overpayment && data.overpayment > 0.01) {
          toast.success(`₺${amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${paymentMethodText} ile alındı. Borç kapatıldı, ₺${data.overpayment.toLocaleString('tr-TR', {minimumFractionDigits: 2})} müşteri alacaklı.`);
        } else if (data.isFullyPaid) {
          toast.success(`₺${amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${paymentMethodText} ile ödendi! Borç tamamen kapatıldı.`);
        } else {
          toast.success(`₺${amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${paymentMethodText} ile tahsil edildi. Kalan borç: ₺${data.remaining?.toFixed(2)}`);
        }

        const effectiveStoreType = userStoreType || storeTypeFilter;
        fetchDebtSummary(effectiveStoreType);
        fetchCreditSummary(effectiveStoreType);
        setShowPaymentModal(false);
        setSelectedDebt(null);
        setPaymentAmount('');
        setMixedPayments([]); // Karışık ödemeleri sıfırla
        setSelectedBankAccount(null); // Seçili banka hesabını sıfırla
        setScrapPaymentMode(false);
        setScrapPaymentData({
          gross_weight: '',
          waste_percent: '',
          waste_gram: '',
          purity: isGoldStore ? '22' : '925',
          unit_price: ''
        });
      }
    } catch (error) {
      console.error('Borç güncelleme hatası:', error);
      toast.error('İşlem başarısız!');
      logService.log('customers', 'debt_payment', `Borç tahsilat hatası: ${selectedCustomer?.name}`, {
        status: 'error',
        details: { error: error.message }
      });
    }
  };

  const fetchCustomers = async (pageNum = 1, reset = false, search = '', sortDebt = false, storeType = 'all', sortCredit = false, sortBalance = false) => {
    try {
      if (reset) {
        setLoading(true);
        setCustomers([]);
        setPage(1);
        setHasMore(true);
      }

      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const sortParam = sortDebt ? '&sortByDebt=true' : sortCredit ? '&sortByCredit=true' : sortBalance ? `&sortByBalance=${sortBalance}` : '';
      const storeTypeParam = storeType && storeType !== 'all' ? `&storeType=${encodeURIComponent(storeType)}` : '';
      const response = await fetch(`${API_URL}/customers?page=${pageNum}&limit=${ITEMS_PER_PAGE}${searchParam}${sortParam}${storeTypeParam}`);
      const data = await response.json();

      if (reset) {
        setCustomers(data.customers || data);
      } else {
        // Sayfa eklerken id'ye göre tekilleştir: sıralama sınırında aynı müşteri iki
        // sayfaya düşse bile listede bir kez görünsün (çift satır koruması).
        setCustomers(prev => {
          const seen = new Set(prev.map(c => c.id));
          const incoming = (data.customers || data).filter(c => !seen.has(c.id));
          return [...prev, ...incoming];
        });
      }

      // Toplam sayı ve daha fazla var mı kontrolü
      if (data.total !== undefined) {
        setTotalCustomers(data.total);
        setHasMore(pageNum * ITEMS_PER_PAGE < data.total);
      } else {
        // Eski API formatı için
        setHasMore((data.customers || data).length === ITEMS_PER_PAGE);
      }

      setPage(pageNum);
    } catch (error) {
      console.error('Müşteri yükleme hatası:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreCustomers = () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const effectiveStoreType = userStoreType || storeTypeFilter;
    fetchCustomers(page + 1, false, searchTerm, sortByDebt, effectiveStoreType, sortByCredit, sortByBalance);
  };
  loadMoreRef.current = loadMoreCustomers;

  // Borç sıralaması toggle
  const toggleDebtSort = () => {
    const newSortByDebt = !sortByDebt;
    setSortByDebt(newSortByDebt);
    if (newSortByDebt) { setSortByCredit(false); setSortByBalance(''); }
    const effectiveStoreType = userStoreType || storeTypeFilter;
    fetchCustomers(1, true, searchTerm, newSortByDebt, effectiveStoreType, false, false);
  };

  // Alacak sıralaması toggle
  const toggleCreditSort = () => {
    const newSortByCredit = !sortByCredit;
    setSortByCredit(newSortByCredit);
    if (newSortByCredit) { setSortByDebt(false); setSortByBalance(''); }
    const effectiveStoreType = userStoreType || storeTypeFilter;
    fetchCustomers(1, true, searchTerm, false, effectiveStoreType, newSortByCredit, false);
  };

  // Bakiye sıralaması toggle (3 aşama: kapalı → borçlu önce → alacaklı önce → kapalı)
  const toggleBalanceSort = () => {
    let newSort = '';
    if (!sortByBalance) newSort = 'debt_first';
    else if (sortByBalance === 'debt_first') newSort = 'credit_first';
    else newSort = '';
    setSortByBalance(newSort);
    if (newSort) { setSortByDebt(false); setSortByCredit(false); }
    const effectiveStoreType = userStoreType || storeTypeFilter;
    fetchCustomers(1, true, searchTerm, false, effectiveStoreType, false, newSort);
  };

  // Shopify müşterilerini getir
  const fetchShopifyCustomers = async (search = '') => {
    setLoadingShopifyCustomers(true);
    try {
      const url = search
        ? `${API_URL}/shopify/customers?query=${encodeURIComponent(search)}`
        : `${API_URL}/shopify/customers`;
      const response = await fetch(url);
      const data = await response.json();
      setShopifyCustomers(data.customers || data || []);
      if (!search) {
        setShopifyCustomersCount(data.count || (data.customers || data || []).length);
      }
    } catch (error) {
      console.error('Shopify müşteri yükleme hatası:', error);
      setShopifyCustomers([]);
    } finally {
      setLoadingShopifyCustomers(false);
    }
  };

  // Shopify müşteri sayısını al (sayfa yüklenirken)
  const fetchShopifyCustomersCount = async () => {
    try {
      const response = await fetch(`${API_URL}/shopify/customers/count`);
      const data = await response.json();
      setShopifyCustomersCount(data.count || 0);
    } catch (error) {
      console.error('Shopify müşteri sayısı alınamadı:', error);
      setShopifyCustomersCount(0);
    }
  };

  // Müşterinin satış geçmişini getir
  const fetchCustomerSales = async (customer) => {
    setSelectedCustomer(customer);
    setShowSalesModal(true);
    setSalesModalTab('sales');
    setExpandedHistoryItems(new Set());
    setLoadingSales(true);

    try {
      const response = await fetch(`${API_URL}/customers/${customer.id}/sales`);
      const data = await response.json();
      setCustomerSales(data);
    } catch (error) {
      console.error('Satış geçmişi yükleme hatası:', error);
      setCustomerSales([]);
    } finally {
      setLoadingSales(false);
    }

    // Tahsilat geçmişini de yükle
    fetchCustomerPayments(customer.id);

    // TL Bakiye geçmişini yükle (altın/gümüş bağımsız)
    try {
      const debtsRes = await fetch(`${API_URL}/customers/${customer.id}/debts`);
      const debtsData = await debtsRes.json();
      const allTx = debtsData.transactions || [];
      const tlTransactions = allTx.filter(tx => (tx.debt_type || tx.currency_type || 'TL') === 'TL');
      const sortedTx = [...tlTransactions].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      let runningBalance = 0;
      const historyWithBalance = sortedTx.map(tx => {
        const prevBalance = runningBalance;
        runningBalance += parseFloat(tx.amount_tl) || 0;
        return { ...tx, prev_balance: prevBalance, new_balance: runningBalance };
      });
      setBalanceHistory(historyWithBalance);
      setCustomerBalance(debtsData.tl_balance ?? debtsData.balance ?? 0);

      // Gram/Gümüş bakiye geçmişi
      const gramGumusTransactions = allTx.filter(tx => {
        const t = tx.debt_type || tx.currency_type || 'TL';
        return t === 'GRAM' || t === 'GUMUS';
      });
      const sortedGramTx = [...gramGumusTransactions].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      let runningGram = 0;
      let runningGumus = 0;
      const gramHistoryWithBalance = sortedGramTx.map(tx => {
        const t = tx.debt_type || tx.currency_type;
        const amt = parseFloat(tx.amount) || 0;
        const prevGram = runningGram;
        const prevGumus = runningGumus;
        if (t === 'GRAM') runningGram += amt;
        else if (t === 'GUMUS') runningGumus += amt;
        return { ...tx, prev_gram: prevGram, prev_gumus: prevGumus, new_gram: runningGram, new_gumus: runningGumus };
      });
      setGramBalanceHistory(gramHistoryWithBalance);
      setGramBalance(debtsData.gram_balance ?? 0);
      setGumusBalance(debtsData.gumus_balance ?? 0);
    } catch (e) {
      setBalanceHistory([]);
      setGramBalanceHistory([]);
      setCustomerBalance(0);
      setGramBalance(0);
      setGumusBalance(0);
    }
  };

  // Müşterinin tahsilat geçmişini getir
  const fetchCustomerPayments = async (customerId) => {
    setLoadingPayments(true);
    try {
      const response = await fetch(`${API_URL}/customers/${customerId}/payments`);
      const data = await response.json();
      setCustomerPayments(data.payments || []);
    } catch (error) {
      console.error('Tahsilat geçmişi yükleme hatası:', error);
      setCustomerPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  // Telefon numarasına göre müşteri kontrol et
  const checkPhoneExists = async (phone) => {
    if (!phone || phone.replace(/\D/g, '').length < 5) {
      setExistingCustomerByPhone(null);
      return;
    }
    try {
      const cleanPhone = phone.replace(/\D/g, '');
      const response = await fetch(`${API_URL}/customers?search=${encodeURIComponent(cleanPhone)}&limit=10`);
      const data = await response.json();
      const customers = data.customers || data || [];
      // Telefon numarasına VE aynı mağaza tipine tam eşleşen müşteri ara.
      // Aynı numarayla en fazla 2 cari olabilir (biri Altın, biri Gümüş) - farklı
      // mağaza tipindeki eşleşme burada engel sayılmaz, backend zaten kuralı uygular.
      const match = customers.find(c =>
        c.phone && c.phone.replace(/\D/g, '') === cleanPhone &&
        (c.store_type || null) === (newCustomer.store_type || null)
      );
      setExistingCustomerByPhone(match || null);
    } catch (error) {
      console.error('Telefon kontrol hatası:', error);
      setExistingCustomerByPhone(null);
    }
  };

  // Telefon değiştiğinde kontrol et
  const handlePhoneChange = (phone) => {
    const digits = normalizePhoneDigits(phone);
    setNewCustomer({ ...newCustomer, phone: digits });
    // Debounce ile kontrol et
    clearTimeout(window.phoneCheckTimeout);
    window.phoneCheckTimeout = setTimeout(() => {
      checkPhoneExists(digits);
    }, 500);
  };

  // Yeni müşteri ekle
  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.phone) {
      toast.warning('Ad Soyad ve Telefon alanları zorunludur!');
      return;
    }
    if (!isPhoneComplete(newCustomer.phone)) {
      toast.warning('Telefon numarası 11 haneli olmalı (0 dahil)');
      return;
    }

    // KVKK onayı kontrolü
    if (!newCustomer.kvkk_consent) {
      toast.warning('Devam etmek için KVKK metnini onaylamanız gerekmektedir!');
      return;
    }

    // Mevcut müşteri varsa uyar
    if (existingCustomerByPhone) {
      toast.error(`Bu telefon numarası zaten "${existingCustomerByPhone.name}" müşterisine kayıtlı!`);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCustomer)
      });
      const data = await response.json();
      if (data.success) {
        logService.log('customers', 'create', `Yeni müşteri eklendi: ${newCustomer.name}${newCustomer.phone ? ' - Tel: ' + newCustomer.phone : ''}`, {
          entity_type: 'customer',
          entity_id: data.customer?.id,
          details: {
            musteri: newCustomer.name,
            telefon: newCustomer.phone || '-',
            email: newCustomer.email || '-',
            adres: newCustomer.address || '-'
          },
          status: 'success'
        });
        setShowModal(false);
        setNewCustomer({ name: '', store_type: '', phone: '', email: '', address: '', tax_number: '', birthday: '', sms_consent: false, kvkk_consent: false });
        setExistingCustomerByPhone(null);
        const effectiveStoreType = userStoreType || storeTypeFilter;
        fetchCustomers(1, true, searchTerm, sortByDebt, effectiveStoreType, sortByCredit, sortByBalance);
        fetchStoreTypeCounts();
      } else {
        toast.error(data.message || 'Müşteri eklenemedi!');
      }
    } catch (error) {
      console.error('Müşteri ekleme hatası:', error);
      toast.error('Müşteri eklenemedi!');
      logService.log('customers', 'create', `Müşteri eklenemedi: ${newCustomer.name}`, { status: 'error', details: { error: error.message } });
    }
  };

  // Düzenleme modalını aç
  const openEditModal = (customer) => {
    const customerData = {
      id: customer.id,
      name: customer.name || '',
      store_type: customer.store_type || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      tax_number: customer.tax_number || '',
      birthday: customer.birthday ? customer.birthday.slice(0, 10) : ''
    };
    setEditingCustomer(customerData);
    setOriginalCustomer({ ...customerData });
    setShowEditModal(true);
  };

  // Müşteri güncelle
  const handleUpdateCustomer = async () => {
    if (!editingCustomer.name) {
      toast.warning('Müşteri adı gerekli!');
      return;
    }
    if (editingCustomer.phone && !isPhoneComplete(editingCustomer.phone)) {
      toast.warning('Telefon numarası 11 haneli olmalı (0 dahil)');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/customers/${editingCustomer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingCustomer.name,
          store_type: editingCustomer.store_type,
          phone: editingCustomer.phone,
          email: editingCustomer.email,
          address: editingCustomer.address,
          tax_number: editingCustomer.tax_number,
          birthday: editingCustomer.birthday
        })
      });
      const data = await response.json();
      if (data.success) {
        // Değişiklikleri tespit et
        const changes = [];
        const fieldNames = {
          name: 'Ad',
          store_type: 'Mağaza',
          phone: 'Telefon',
          email: 'E-posta',
          address: 'Adres',
          tax_number: 'Vergi No',
          birthday: 'Doğum Tarihi'
        };

        Object.keys(fieldNames).forEach(field => {
          const oldVal = originalCustomer?.[field] || '';
          const newVal = editingCustomer[field] || '';
          if (oldVal !== newVal) {
            if (oldVal && newVal) {
              changes.push(`${fieldNames[field]}: "${oldVal}" → "${newVal}"`);
            } else if (newVal) {
              changes.push(`${fieldNames[field]} eklendi: "${newVal}"`);
            } else {
              changes.push(`${fieldNames[field]} silindi`);
            }
          }
        });

        const changeDesc = changes.length > 0
          ? changes.join(', ')
          : 'Değişiklik yapılmadı';

        logService.log('customers', 'update', `${editingCustomer.name} - ${changeDesc}`, {
          entity_type: 'customer',
          entity_id: editingCustomer.id,
          details: {
            musteri: editingCustomer.name,
            degisiklikler: changes,
            onceki: originalCustomer,
            sonraki: {
              name: editingCustomer.name,
              phone: editingCustomer.phone,
              email: editingCustomer.email,
              address: editingCustomer.address
            }
          },
          status: 'success'
        });
        setShowEditModal(false);
        setEditingCustomer(null);
        setOriginalCustomer(null);
        const effectiveStoreType = userStoreType || storeTypeFilter;
        fetchCustomers(1, true, searchTerm, sortByDebt, effectiveStoreType, sortByCredit, sortByBalance);
        fetchStoreTypeCounts();
        toast.success('Müşteri başarıyla güncellendi!');
      } else {
        toast.error('Müşteri güncellenemedi!');
        logService.log('customers', 'update', `Müşteri güncellenemedi: ${editingCustomer.name}`, { status: 'error' });
      }
    } catch (error) {
      console.error('Müşteri güncelleme hatası:', error);
      toast.error('Müşteri güncellenemedi!');
      logService.log('customers', 'update', `Müşteri güncelleme hatası`, { status: 'error', details: { error: error.message } });
    }
  };

  // Müşteri sil (sadece admin)
  const handleDeleteCustomer = async (customer) => {
    if (user?.role !== 'admin') { toast.error('Bu işlem sadece admin yetkisiyle yapılabilir'); return; }
    showConfirmation({
      title: 'Müşteri Sil',
      message: `"${customer.name}" adlı müşteriyi silmek istediğinize emin misiniz?`,
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/customers/${customer.id}`, {
            method: 'DELETE'
          });
          const data = await response.json();

          if (data.success) {
            toast.success('Müşteri silindi!');
            logService.log('customers', 'delete', `Müşteri silindi: ${customer.name}${customer.phone ? ' - Tel: ' + customer.phone : ''}`, {
              entity_type: 'customer',
              entity_id: customer.id,
              details: {
                musteri: customer.name,
                telefon: customer.phone || '-',
                email: customer.email || '-'
              },
              status: 'success'
            });
            setCustomers(prev => prev.filter(c => c.id !== customer.id));
            setTotalCustomers(prev => prev - 1);
          } else {
            toast.error(data.message || 'Müşteri silinemedi!');
            logService.log('customers', 'delete', `Müşteri silinemedi: ${customer.name}`, { status: 'error', details: { message: data.message } });
          }
        } catch (error) {
          console.error('Müşteri silme hatası:', error);
          toast.error('Müşteri silinemedi!');
          logService.log('customers', 'delete', `Müşteri silme hatası: ${customer.name}`, { status: 'error', details: { error: error.message } });
        }
      }
    });
  };

  // Toplu silme - müşteri seçimi toggle
  const toggleCustomerSelection = (customerId) => {
    // "Tümünü seç" modundayken tek müşteri çıkarılırsa
    if (selectAllFiltered) {
      setSelectAllFiltered(false);
      // Yüklü tüm müşterileri seç, sadece tıklananı çıkar
      setSelectedCustomers(customers.map(c => c.id).filter(id => id !== customerId));
      return;
    }

    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  // Tümünü seç/kaldır
  const toggleSelectAll = () => {
    if (selectAllFiltered) {
      // Tümü seçiliydi, kaldır
      setSelectAllFiltered(false);
      setSelectedCustomers([]);
    } else {
      // Filtredeki tüm müşterileri seç
      setSelectAllFiltered(true);
      setSelectedCustomers(customers.map(c => c.id));
    }
  };

  // Toplu silme işlemi
  const handleBulkDelete = async () => {
    if (!selectAllFiltered && selectedCustomers.length === 0) {
      toast.warning('Lütfen silmek istediğiniz müşterileri seçin!');
      return;
    }

    const deletePayload = {
      deleteAll: selectAllFiltered,
      customerIds: selectAllFiltered ? [] : selectedCustomers,
      storeType: storeTypeFilter !== 'all' ? storeTypeFilter : '',
      search: searchTerm || ''
    };

    // Önce ön kontrol yap - borçlu müşteri var mı?
    try {
      const previewResponse = await fetch(`${API_URL}/customers/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...deletePayload, previewOnly: true })
      });
      const preview = await previewResponse.json();

      let confirmMessage = `${preview.totalToDelete} müşteriyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz!`;
      if (preview.debtCustomerCount > 0) {
        confirmMessage = `${preview.totalToDelete} müşteriden ${preview.debtCustomerCount} tanesinin ödenmemiş borcu var!\n\nBorçlu müşteriler de dahil tümü silinecek. Devam etmek istiyor musunuz?`;
      }

      showConfirmation({
        title: 'Toplu Müşteri Silme',
        message: confirmMessage,
        confirmText: 'Evet, Sil',
        cancelText: 'İptal',
        type: 'danger',
        onConfirm: async () => {
          try {
            toast.info('Müşteriler siliniyor, lütfen bekleyin...');
            const response = await fetch(`${API_URL}/customers/bulk-delete`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(deletePayload)
            });
            const result = await response.json();

            if (result.deleted > 0) {
              toast.success(`${result.deleted} müşteri silindi!`);
              logService.log('customers', 'bulk_delete', `Toplu silme: ${result.deleted} müşteri silindi`, {
                status: 'success',
                details: { deleted_count: result.deleted, error_count: result.errors }
              });
            }
            if (result.errors > 0) {
              toast.error(`${result.errors} müşteri silinemedi!`);
            }
          } catch (error) {
            console.error('Toplu silme hatası:', error);
            toast.error('Silme işlemi sırasında hata oluştu!');
          }

          // Temizle ve yenile
          setSelectedCustomers([]);
          setSelectAllFiltered(false);
          setBulkDeleteMode(false);
          const effectiveStoreType = userStoreType || storeTypeFilter;
          fetchCustomers(1, true, searchTerm, sortByDebt, effectiveStoreType, sortByCredit, sortByBalance);
          fetchStoreTypeCounts();
          fetchDebtSummary(storeTypeFilter !== 'all' ? storeTypeFilter : 'all');
          fetchCreditSummary(storeTypeFilter !== 'all' ? storeTypeFilter : 'all');
        }
      });
    } catch (error) {
      console.error('Ön kontrol hatası:', error);
      toast.error('İşlem sırasında hata oluştu!');
    }
  };

  // Toplu silme iptal
  const cancelBulkDelete = () => {
    setSelectedCustomers([]);
    setSelectAllFiltered(false);
    setBulkDeleteMode(false);
  };

  // Excel'den müşteri içe aktar
  // Format: Mağaza (Altın/Gümüş) | Ad Soyad | Telefon | E-posta | Borç | Para Birimi
  // Excel dosyasını parse et ve ön izleme göster
  const handleExcelImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const customers = [];
          let startRow = 0;

          if (jsonData.length > 0) {
            const firstRow = jsonData[0];
            const firstCell = String(firstRow[0] || '').toLowerCase().trim();
            if (firstCell === 'mağaza' || firstCell === 'magaza' || firstCell === 'store') {
              startRow = 1;
            }
          }

          for (let i = startRow; i < jsonData.length; i++) {
            const row = jsonData[i];

            if (row.length >= 2) {
              const storeType = String(row[0] || '').trim();
              const name = String(row[1] || '').trim();
              const phone = row[2] ? String(row[2]).trim() : '';
              const email = row[3] ? String(row[3]).trim() : '';
              const debtAmount = row[4] ? parseFloat(String(row[4]).replace(',', '.')) : 0;
              let currency = row[5] ? String(row[5]).trim().toUpperCase() : 'TL';

              if (currency === 'ALTIN' || currency === 'GOLD' || currency === 'GR ALTIN' || currency === 'GRAM ALTIN' || currency === 'GRAM') {
                currency = 'GRAM';
              } else if (currency === 'GÜMÜŞ' || currency === 'GUMUS' || currency === 'SILVER' || currency === 'GR GÜMÜŞ' || currency === 'GRAM GÜMÜŞ') {
                currency = 'GUMUS';
              } else {
                currency = 'TL';
              }

              if (name) {
                let normalizedStoreType = null;
                const storeTypeLower = storeType.toLowerCase();
                if (storeTypeLower === 'altın' || storeTypeLower === 'altin' || storeTypeLower === 'gold') {
                  normalizedStoreType = 'Altın';
                } else if (storeTypeLower === 'gümüş' || storeTypeLower === 'gumus' || storeTypeLower === 'silver') {
                  normalizedStoreType = 'Gümüş';
                }

                customers.push({
                  store_type: normalizedStoreType,
                  name: name,
                  phone: phone,
                  email: email,
                  debt_amount: debtAmount > 0 ? debtAmount : null,
                  debt_currency: debtAmount > 0 ? currency : null
                });
              }
            }
          }

          if (customers.length === 0) {
            toast.warning('Excel dosyasında geçerli müşteri bulunamadı!');
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
          }

          // Ön izleme verisi al
          const previewResponse = await fetch(`${API_URL}/customers/bulk-preview`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ customers })
          });

          const previewResult = await previewResponse.json();

          if (previewResult.success) {
            setParsedCustomers(customers);
            setImportPreviewData(previewResult);
            setShowImportPreview(true);
          } else {
            toast.error(previewResult.message || 'Ön izleme alınamadı!');
          }
        } catch (parseError) {
          console.error('Excel parse hatası:', parseError);
          toast.error('Excel dosyası okunamadı!');
        } finally {
          setImporting(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Excel import hatası:', error);
      toast.error('Dosya yüklenirken hata oluştu!');
      setImporting(false);
    }
  };

  // Ön izleme onaylandıktan sonra gerçek import işlemi
  const handleConfirmImport = async () => {
    setImporting(true);
    try {
      const customers = parsedCustomers;

      const response = await fetch(`${API_URL}/customers/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customers })
      });

      const result = await response.json();

      if (result.success) {
        let debtCount = 0;

        // Yeni eklenen müşteriler için borç oluştur
        if (result.createdCustomers && result.createdCustomers.length > 0) {
          for (const createdCustomer of result.createdCustomers) {
            const originalData = customers.find(c => {
              return c.name.toLowerCase().trim() === createdCustomer.name.toLowerCase().trim();
            });

            if (originalData && originalData.debt_amount && originalData.debt_amount > 0) {
              try {
                const debtResponse = await fetch(`${API_URL}/customers/${createdCustomer.id}/debts`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    debt_type: originalData.debt_currency,
                    amount: originalData.debt_amount,
                    amount_tl: originalData.debt_currency === 'TL' ? originalData.debt_amount : originalData.debt_amount,
                    description: `Excel ile içe aktarıldı`
                  })
                });
                const debtResult = await debtResponse.json();
                if (debtResult.success) debtCount++;
              } catch (debtError) {
                console.error('Borç ekleme hatası:', debtError);
              }
            }
          }
        }

        let message = '';
        if (result.imported > 0) message += `${result.imported} müşteri eklendi`;
        if (result.updated > 0) message += `${message ? ', ' : ''}${result.updated} müşteri güncellendi`;
        if (debtCount > 0) message += ` (${debtCount} müşteriye borç eklendi)`;
        if (!message) message = 'İşlem tamamlandı, değişiklik yapılmadı.';

        toast.success(message);
        logService.log('customers', 'import', `Excel'den müşteri içe aktarıldı: ${result.imported} yeni, ${result.updated} güncelleme`, {
          status: 'success',
          details: {
            imported_count: result.imported,
            updated_count: result.updated,
            debt_count: debtCount,
            total_in_file: customers.length
          }
        });
        const effectiveStoreType = userStoreType || storeTypeFilter;
        fetchCustomers(1, true, searchTerm, sortByDebt, effectiveStoreType, sortByCredit, sortByBalance);
      } else {
        toast.error(result.message || 'İçe aktarma başarısız!');
        logService.log('customers', 'import', 'Müşteri içe aktarma başarısız', {
          status: 'error',
          details: { error: result.message }
        });
      }
    } catch (error) {
      console.error('Excel import hatası:', error);
      toast.error('İçe aktarma sırasında hata oluştu!');
    } finally {
      setImporting(false);
      setShowImportPreview(false);
      setImportPreviewData(null);
      setParsedCustomers([]);
    }
  };

  // Excel'e dışarı aktar
  // Format: Mağaza | Ad Soyad | Telefon | E-posta
  const handleExcelExport = async () => {
    try {
      toast.info('Müşteriler dışa aktarılıyor...');

      // Tüm müşterileri ve borçları paralel getir
      const [customersResponse, debtsResponse] = await Promise.all([
        fetch(`${API_URL}/customers?page=1&limit=100000`),
        fetch(`${API_URL}/customers/debts/all`)
      ]);
      const customersData = await customersResponse.json();
      const allDebts = await debtsResponse.json();
      const allCustomers = customersData.customers || customersData;

      if (allCustomers.length === 0) {
        toast.warning('Dışa aktarılacak müşteri bulunamadı!');
        return;
      }

      // Ödenmemiş borçları müşteri bazında grupla (tür bazında topla)
      const debtsByCustomer = {};
      (Array.isArray(allDebts) ? allDebts : []).forEach(debt => {
        if (debt.is_paid) return;
        if (!debtsByCustomer[debt.customer_id]) {
          debtsByCustomer[debt.customer_id] = { TL: 0, GRAM: 0, GUMUS: 0 };
        }
        const type = debt.debt_type || 'TL';
        debtsByCustomer[debt.customer_id][type] = (debtsByCustomer[debt.customer_id][type] || 0) + parseFloat(debt.amount || 0);
      });

      // Excel için veri hazırla
      const exportData = allCustomers.map(customer => {
        const debts = debtsByCustomer[customer.id];
        let debtAmount = '';
        let debtCurrency = '';

        if (debts) {
          // Birden fazla borç türü varsa ayrı satırlar yerine en büyük borcu göster
          // veya hepsini virgülle ayır
          const debtParts = [];
          if (debts.TL > 0) debtParts.push({ amount: debts.TL, currency: 'TL' });
          if (debts.GRAM > 0) debtParts.push({ amount: debts.GRAM, currency: 'ALTIN' });
          if (debts.GUMUS > 0) debtParts.push({ amount: debts.GUMUS, currency: 'GÜMÜŞ' });

          if (debtParts.length === 1) {
            debtAmount = debtParts[0].amount;
            debtCurrency = debtParts[0].currency;
          } else if (debtParts.length > 1) {
            // Birden fazla tür varsa ilk borcu ana sütuna, diğerlerini ek satır olarak
            debtAmount = debtParts.map(d => d.amount).join(' / ');
            debtCurrency = debtParts.map(d => d.currency).join(' / ');
          }
        }

        return {
          'Mağaza': customer.store_type || '',
          'Ad Soyad': customer.name || '',
          'Telefon': customer.phone || '',
          'E-posta': customer.email || '',
          'Borç': debtAmount,
          'Para Birimi': debtCurrency
        };
      });

      // Excel dosyası oluştur
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Müşteriler');

      // Sütun genişliklerini ayarla
      worksheet['!cols'] = [
        { wch: 12 }, // Mağaza
        { wch: 25 }, // Ad Soyad
        { wch: 15 }, // Telefon
        { wch: 25 }, // E-posta
        { wch: 15 }, // Borç
        { wch: 15 }  // Para Birimi
      ];

      // Dosyayı indir
      const fileName = `musteriler_${new Date().toISOString().slice(0,10)}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success(`${allCustomers.length} müşteri dışa aktarıldı!`);
    } catch (error) {
      console.error('Excel export hatası:', error);
      toast.error('Dışa aktarma başarısız!');
    }
  };

  // Örnek şablon indir
  const handleDownloadTemplate = () => {
    const templateData = [
      { 'Mağaza': 'Altın', 'Ad Soyad': 'Örnek Müşteri 1', 'Telefon': '05551234567', 'E-posta': 'ornek1@email.com', 'Borç': '1500.50', 'Para Birimi': 'TL' },
      { 'Mağaza': 'Altın', 'Ad Soyad': 'Örnek Müşteri 2', 'Telefon': '05559876543', 'E-posta': 'ornek2@email.com', 'Borç': '2.50', 'Para Birimi': 'ALTIN' },
      { 'Mağaza': 'Gümüş', 'Ad Soyad': 'Örnek Müşteri 3', 'Telefon': '05551112233', 'E-posta': '', 'Borç': '5.00', 'Para Birimi': 'GÜMÜŞ' },
      { 'Mağaza': 'Altın', 'Ad Soyad': 'Örnek Müşteri 4', 'Telefon': '05554443322', 'E-posta': '', 'Borç': '', 'Para Birimi': '' }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Müşteriler');

    // Sütun genişliklerini ayarla
    worksheet['!cols'] = [
      { wch: 12 }, // Mağaza
      { wch: 25 }, // Ad Soyad
      { wch: 15 }, // Telefon
      { wch: 25 }, // E-posta
      { wch: 12 }, // Borç
      { wch: 12 }  // Para Birimi
    ];

    XLSX.writeFile(workbook, 'musteri_sablonu.xlsx');
    toast.success('Örnek şablon indirildi!');
  };

  // Telefon numarasını formatla: 555 555 55 55
  const formatPhone = (phone) => {
    if (!phone) return '-';
    // Sadece rakamları al
    let digits = phone.replace(/\D/g, '');
    // Başındaki 0'ı kaldır
    if (digits.startsWith('0')) {
      digits = digits.substring(1);
    }
    // 10 haneli numara için formatlama: 555 555 55 55
    if (digits.length === 10) {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
    }
    // Diğer uzunluklar için olduğu gibi döndür
    return digits || '-';
  };

  const stats = {
    total: totalCustomers || customers.length
  };

  // Birleşik müşteri geçmişi (satışlar + tahsilatlar tarih sırasına göre)
  const mergedHistory = useMemo(() => {
    const salesItems = customerSales.map(sale => ({
      ...sale,
      _type: 'sale',
      _key: `sale-${sale.id}`,
      _date: new Date(sale.created_at)
    }));
    const paymentItems = customerPayments.map(payment => ({
      ...payment,
      _type: 'payment',
      _key: `payment-${payment.id}`,
      _date: new Date(payment.created_at)
    }));
    return [...salesItems, ...paymentItems].sort((a, b) => b._date - a._date);
  }, [customerSales, customerPayments]);

  // Müşteri geçmişi dip toplamlar
  const historySummary = useMemo(() => {
    const calcSaleAmount = (sale) => {
      const total = Number(sale.total) || 0;
      const subtotal = Number(sale.subtotal) || 0;
      const discount = Number(sale.discount) || 0;
      const scrapAmount = Number(sale.scrap_total_amount) || 0;
      const scrapDebt = Number(sale.scrap_debt_discount) || 0;
      if (discount > 0 && subtotal > 0 && Math.abs(total - subtotal) < 1) {
        return total - discount - scrapDebt;
      }
      return total + scrapAmount;
    };
    const totalSales = customerSales.reduce((sum, sale) => {
      if (sale.sale_type === 'refund' || sale.status === 'refunded') return sum;
      return sum + calcSaleAmount(sale);
    }, 0);
    const totalRefunds = customerSales.reduce((sum, sale) => {
      if (sale.sale_type === 'refund' || sale.status === 'refunded') return sum + Math.abs(calcSaleAmount(sale));
      return sum;
    }, 0);
    // Güncel bakiye: debtSummary - creditSummary
    const custId = selectedCustomer?.id;
    const debt = custId && debtSummary[custId];
    const credit = custId && creditSummary[custId];
    const tlDebt = debt ? Number(debt.total_tl_debt) || 0 : 0;
    const tlCredit = credit ? Number(credit.total_tl_credit) || 0 : 0;
    const netBalance = tlDebt - tlCredit;

    // Tahsilat = müşteriden alınan toplam ödeme (nakit/kart/havale dahil):
    // Net alışveriş (alışveriş − iade) − güncel bakiye (borç düşülür, alacak eklenir)
    const totalPayments = Math.max(0, (totalSales - totalRefunds) - netBalance);

    return { totalSales, totalRefunds, totalPayments, netBalance };
  }, [customerSales, selectedCustomer, debtSummary, creditSummary]);

  const toggleHistoryItem = (key) => {
    setExpandedHistoryItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Fiş detayını modal olarak aç
  const openSaleDetailInModal = async (saleId) => {
    setSaleDetailLoading(true);
    setSaleDetailModal(null);
    try {
      const response = await fetch(`${API_URL}/sales/${saleId}`);
      const data = await response.json();
      setSaleDetailModal(data);
    } catch (error) {
      console.error('Fiş detay hatası:', error);
      toast.error('Fiş detayı yüklenemedi');
    } finally {
      setSaleDetailLoading(false);
    }
  };

  const getStatusBadge = (sale) => {
    if (sale.sale_type === 'refund' || sale.status === 'refunded') {
      return <span className="status-badge refunded">İade</span>;
    }
    switch(sale.status) {
      case 'partial_refund': return <span className="status-badge partial">Kısmi İade</span>;
      default: return <span className="status-badge completed">Tamamlandı</span>;
    }
  };

  return (
    <div className={`customers-container ${sideMenuOpen ? 'menu-open' : ''}`}>
      {/* Sol Yan Menü */}
      <div className={`side-menu ${sideMenuOpen ? 'open' : ''}`}>
        <div className="side-menu-header">
          <h3>İşlemler</h3>
        </div>
        <div className="side-menu-content">
          {/* Müşteri İşlemleri Kategorisi */}
          <div className="menu-category">
            <button
              className={`menu-category-header ${menuAccordion.customers ? 'open' : ''}`}
              onClick={() => setMenuAccordion(prev => ({ ...prev, customers: !prev.customers }))}
            >
              <div className="menu-category-title">
                <Users size={18} />
                <span>Müşteri İşlemleri</span>
              </div>
              {menuAccordion.customers ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            <div className={`menu-category-items ${menuAccordion.customers ? 'open' : ''}`}>
              <button className="side-menu-subitem" onClick={() => { setShowModal(true); setSideMenuOpen(false); }}>
                Yeni Müşteri Ekle
              </button>
              <button className="side-menu-subitem" onClick={() => { setShowLogModal(true); setSideMenuOpen(false); }}>
                İşlem Geçmişi
              </button>
              {!bulkDeleteMode && user?.role === 'admin' && (
                <button className="side-menu-subitem danger" onClick={() => { setBulkDeleteMode(true); setSideMenuOpen(false); }}>
                  <Trash2 size={14} /> Toplu Müşteri Sil
                </button>
              )}
            </div>
          </div>

          {/* Excel İşlemleri Kategorisi */}
          <div className="menu-category">
            <button
              className={`menu-category-header ${menuAccordion.excel ? 'open' : ''}`}
              onClick={() => setMenuAccordion(prev => ({ ...prev, excel: !prev.excel }))}
            >
              <div className="menu-category-title">
                <FileSpreadsheet size={18} />
                <span>Excel İşlemleri</span>
              </div>
              {menuAccordion.excel ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            <div className={`menu-category-items ${menuAccordion.excel ? 'open' : ''}`}>
              <button className="side-menu-subitem" onClick={() => { handleDownloadTemplate(); setSideMenuOpen(false); }}>
                Örnek Şablon İndir
              </button>
              <button className="side-menu-subitem" onClick={() => { fileInputRef.current?.click(); setSideMenuOpen(false); }}>
                İçe Aktar
              </button>
              <button className="side-menu-subitem" onClick={() => { handleExcelExport(); setSideMenuOpen(false); }}>
                Dışa Aktar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Ana İçerik */}
      <div className="main-content">
      <header className="page-header">
        <div className="header-left">
          <button className="hamburger-btn" onClick={toggleSideMenu}>
            <Menu size={20} />
          </button>
          <button className="back-btn" onClick={() => navigate('/')}>
            <ArrowLeft size={16} /> Ana Sayfa
          </button>
          <h1>Müşteri Yönetimi</h1>
        </div>
        <div className="header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <div className="header-actions">
          <input
            type="file"
            ref={fileInputRef}
            accept=".xlsx,.xls"
            onChange={handleExcelImport}
            style={{ display: 'none' }}
          />
          <button className="action-header-btn" onClick={() => setShowModal(true)}>
            <Plus size={16} /> Yeni Müşteri
          </button>
        </div>
      </header>

      {/* Toplu Silme Kontrol Barı */}
      {bulkDeleteMode && (
        <div className="bulk-delete-bar">
          <div className="bulk-delete-info">
            <input
              type="checkbox"
              checked={selectAllFiltered}
              onChange={toggleSelectAll}
              id="selectAllCustomers"
            />
            <label htmlFor="selectAllCustomers">Tümünü Seç ({totalCustomers})</label>
            <span className="selected-count">
              {selectAllFiltered ? `${totalCustomers} müşteri seçildi (tümü)` : `${selectedCustomers.length} müşteri seçildi`}
            </span>
          </div>
          <div className="bulk-delete-actions">
            <button className="btn secondary" onClick={cancelBulkDelete}>İptal</button>
            <button
              className="btn danger"
              onClick={handleBulkDelete}
              disabled={!selectAllFiltered && selectedCustomers.length === 0}
            >
              <Trash2 size={16} /> Seçilenleri Sil ({selectAllFiltered ? totalCustomers : selectedCustomers.length})
            </button>
          </div>
        </div>
      )}

      <div className="customers-content">
        {/* Yardım Butonu */}
        <button className="help-btn-corner" title="Yardım" onClick={() => setShowHelpModal(true)}>?</button>

        {/* Stats */}
        <div className="stats-row">
          {/* Toplam Müşteri - Sadece admin veya tüm mağaza yetkisi olanlar görebilir */}
          {!userStoreType && (
            <div
              className={`stat-card clickable ${!showShopifyCustomers && storeTypeFilter === 'all' ? 'active' : ''}`}
              onClick={() => { setShowShopifyCustomers(false); setShopifySearchTerm(''); setStoreTypeFilter('all'); fetchCustomers(1, true, searchTerm, sortByDebt, 'all', sortByCredit, sortByBalance); }}
            >
              <span className="stat-icon"><Users size={24} /></span>
              <div className="stat-info">
                <span className="stat-value">{stats.total}</span>
                <span className="stat-label">Toplam Müşteri</span>
              </div>
            </div>
          )}
          {/* Shopify Müşteri - Sadece admin veya tüm mağaza yetkisi olanlar görebilir */}
          {!userStoreType && (
            <div
              className={`stat-card clickable shopify ${showShopifyCustomers ? 'active' : ''}`}
              onClick={() => { setShowShopifyCustomers(true); setStoreTypeFilter('all'); fetchShopifyCustomers(); }}
            >
              <span className="stat-icon shopify-icon"><ShoppingCart size={24} /></span>
              <div className="stat-info">
                <span className="stat-value">{shopifyCustomersCount}</span>
                <span className="stat-label">Shopify Müşteri</span>
              </div>
            </div>
          )}
          {/* Altın Müşteriler - Admin veya Altın mağaza kasiyerleri görebilir */}
          {(!userStoreType || userStoreType === 'Altın') && (
            <div
              className={`stat-card ${userStoreType ? '' : 'clickable'} gold ${!showShopifyCustomers && storeTypeFilter === 'Altın' ? 'active' : ''}`}
              onClick={userStoreType ? undefined : () => { setShowShopifyCustomers(false); setShopifySearchTerm(''); setStoreTypeFilter('Altın'); }}
              style={userStoreType ? { cursor: 'default' } : {}}
            >
              <span className="stat-icon gold-icon"><Coins size={24} /></span>
              <div className="stat-info">
                <span className="stat-value">{goldCustomersCount}</span>
                <span className="stat-label">Altın Müşteri</span>
              </div>
            </div>
          )}
          {/* Gümüş Müşteriler - Admin veya Gümüş mağaza kasiyerleri görebilir */}
          {(!userStoreType || userStoreType === 'Gümüş') && (
            <div
              className={`stat-card ${userStoreType ? '' : 'clickable'} silver ${!showShopifyCustomers && storeTypeFilter === 'Gümüş' ? 'active' : ''}`}
              onClick={userStoreType ? undefined : () => { setShowShopifyCustomers(false); setShopifySearchTerm(''); setStoreTypeFilter('Gümüş'); }}
              style={userStoreType ? { cursor: 'default' } : {}}
            >
              <span className="stat-icon silver-icon"><Coins size={24} /></span>
              <div className="stat-info">
                <span className="stat-value">{silverCustomersCount}</span>
                <span className="stat-label">Gümüş Müşteri</span>
              </div>
            </div>
          )}
        </div>

        {/* Toplam Bakiye ve Gram Borç Özeti */}
        {(totalDebts.totalTL > 0 || totalDebts.totalGold > 0 || totalDebts.totalSilver > 0 || totalCredits.totalTL > 0 || totalCredits.totalGold > 0 || totalCredits.totalSilver > 0) && (
          <div className="total-balance-summary">
            <div className="total-balance-items">
              {/* Toplam Alacak */}
              {totalDebts.totalTL > 0 && (
                <span className="total-balance-item positive">
                  <Banknote size={14} />
                  Toplam Alacak: ₺{totalDebts.totalTL.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
              {/* Toplam Verecek */}
              {totalCredits.totalTL > 0 && (
                <>
                  {totalDebts.totalTL > 0 && <span className="balance-divider">|</span>}
                  <span className="total-balance-item negative">
                    <Banknote size={14} />
                    Toplam Verecek: ₺{totalCredits.totalTL.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </>
              )}
              {/* Net TL Bakiye */}
              {(() => {
                const netTL = totalDebts.totalTL - totalCredits.totalTL;
                return (
                  <>
                    {(totalDebts.totalTL > 0 || totalCredits.totalTL > 0) && <span className="balance-divider">|</span>}
                    <span className={`total-balance-item ${netTL >= 0 ? 'positive' : 'negative'}`}>
                      <Banknote size={14} />
                      Net Bakiye: {netTL >= 0 ? '+' : ''}₺{netTL.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </>
                );
              })()}
              {/* Altın Borcu */}
              {totalDebts.totalGold > 0 && (
                <>
                  <span className="balance-divider">|</span>
                  <span className="total-debt-item gold">
                    <Coins size={14} />
                    Altın Borcu: {totalDebts.totalGold.toFixed(2)} gr
                  </span>
                </>
              )}
              {/* Gümüş Borcu */}
              {totalDebts.totalSilver > 0 && (
                <>
                  <span className="balance-divider">|</span>
                  <span className="total-debt-item silver">
                    <Coins size={14} />
                    Gümüş Borcu: {totalDebts.totalSilver.toFixed(2)} gr
                  </span>
                </>
              )}
              <button
                onClick={() => setShowReportModal(true)}
                style={{
                  marginLeft: 'auto',
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.82rem',
                  color: '#475569',
                  fontWeight: 500,
                  transition: 'all 0.2s',
                  flexShrink: 0
                }}
                title="Bakiye Raporu Yazdır"
              >
                <Printer size={14} />
                Rapor
              </button>
            </div>
          </div>
        )}

        {/* Normal Müşteriler veya Shopify Müşterileri */}
        {!showShopifyCustomers ? (
          <>
            {/* Search */}
            <div className="search-section">
              <div className="search-box">
                <span className="search-icon"><Search size={18} /></span>
                <input
                  type="text"
                  placeholder="Müşteri adı, telefon veya email ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
          <div className="column-settings-wrapper">
            <button
              className="column-settings-btn"
              onClick={() => setShowColumnSettings(!showColumnSettings)}
            >
              <Settings size={16} /> Sütunlar
            </button>
            {showColumnSettings && (
              <div className="column-settings-dropdown">
                <div className="column-settings-header">
                  <span>Görünür Sütunlar</span>
                  <button className="reset-columns-btn" onClick={resetColumns}>Sıfırla</button>
                </div>
                <div className="column-settings-list">
                  {allColumns.map(col => (
                    <label key={col.key} className="column-toggle">
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Customer List */}
        {loading ? (
          <div className="loading">Yükleniyor...</div>
        ) : (
          <div className="customers-list">
            <div className="list-header">
              {bulkDeleteMode && (
                <span className="col-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0}
                    onChange={toggleSelectAll}
                    title="Tümünü Seç"
                  />
                </span>
              )}
              {visibleColumns.name && <span className="col-name">Ad Soyad</span>}
              {visibleColumns.phone && <span className="col-phone">Telefon</span>}
              {visibleColumns.email && <span className="col-email">E-posta</span>}
              {visibleColumns.address && <span className="col-address">Adres</span>}
              {visibleColumns.taxNumber && <span className="col-tax">Vergi No</span>}
              {visibleColumns.birthday && <span className="col-birthday">Doğum Tarihi</span>}
              {visibleColumns.balance && (
                <span className={`col-balance sortable ${sortByBalance ? 'active' : ''}`} onClick={toggleBalanceSort}>
                  TL Bakiye {sortByBalance === 'debt_first' ? '↓' : sortByBalance === 'credit_first' ? '↑' : '↕'}
                </span>
              )}
              {visibleColumns.gramDebt && (
                <span className={`col-gram-debt sortable ${sortByDebt ? 'active' : ''}`} onClick={toggleDebtSort}>
                  Gram Borç {sortByDebt ? '↓' : '↕'}
                </span>
              )}
              {visibleColumns.createdAt && <span className="col-date">Kayıt Tarihi</span>}
              <span className="col-actions">İşlemler</span>
            </div>
            <div className="list-body">
              {visibleCustomers.map(customer => (
                <div
                  key={customer.id}
                  className={`list-row ${(debtSummary[customer.id] && (Number(debtSummary[customer.id].total_tl_debt) > 0 || Number(debtSummary[customer.id].total_gold_debt || 0) > 0 || Number(debtSummary[customer.id].total_silver_debt || 0) > 0)) ? 'has-debt' : ''} ${bulkDeleteMode && (selectAllFiltered || selectedCustomers.includes(customer.id)) ? 'selected-for-delete' : ''} ${bulkDeleteMode ? 'clickable-row' : ''}`}
                  onClick={() => bulkDeleteMode && toggleCustomerSelection(customer.id)}
                  onDoubleClick={() => !bulkDeleteMode && fetchCustomerSales(customer)}
                  style={bulkDeleteMode ? { cursor: 'pointer' } : { cursor: 'pointer' }}
                >
                  {bulkDeleteMode && (
                    <span className="col-checkbox" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectAllFiltered || selectedCustomers.includes(customer.id)}
                        onChange={() => toggleCustomerSelection(customer.id)}
                      />
                    </span>
                  )}
                  {visibleColumns.name && (
                    <span className="col-name">
                      <span className="customer-initial">{customer.name?.charAt(0) || '?'}</span>
                      {customer.name}
                    </span>
                  )}
                  {visibleColumns.phone && <span className="col-phone">{formatPhone(customer.phone)}</span>}
                  {visibleColumns.email && <span className="col-email">{customer.email || '-'}</span>}
                  {visibleColumns.address && <span className="col-address">{customer.address || '-'}</span>}
                  {visibleColumns.taxNumber && <span className="col-tax">{customer.tax_number || '-'}</span>}
                  {visibleColumns.birthday && (
                    <span className="col-birthday">
                      {customer.birthday ? new Date(customer.birthday).toLocaleDateString('tr-TR') : '-'}
                    </span>
                  )}
                  {visibleColumns.balance && (() => {
                    const debt = debtSummary[customer.id];
                    const credit = creditSummary[customer.id];
                    const tlDebt = debt ? Number(debt.total_tl_debt) : 0;
                    const tlCredit = credit ? Number(credit.total_tl_credit) : 0;
                    const tlBalance = tlDebt - tlCredit;
                    return (
                      <span className="col-balance" onClick={() => (tlBalance !== 0 || debtSummary[customer.id]) ? fetchCustomerDebts(customer) : null} style={{ cursor: tlBalance !== 0 || debtSummary[customer.id] ? 'pointer' : 'default' }}>
                        {tlBalance !== 0 ? (
                          <span className={`balance-badge ${tlBalance > 0 ? 'positive' : 'negative'}`}>
                            {tlBalance > 0 ? '+' : ''}₺{tlBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        ) : '-'}
                      </span>
                    );
                  })()}
                  {visibleColumns.gramDebt && (() => {
                    const debt = debtSummary[customer.id];
                    const goldDebt = debt ? Number(debt.total_gold_debt || 0) : 0;
                    const silverDebt = debt ? Number(debt.total_silver_debt || 0) : 0;
                    const hasGram = Math.abs(goldDebt) > 0.0001 || Math.abs(silverDebt) > 0.0001;
                    return (
                      <span className="col-gram-debt" onClick={() => hasGram ? fetchCustomerDebts(customer) : null} style={{ cursor: hasGram ? 'pointer' : 'default' }}>
                        {hasGram ? (
                          <span className="gram-debt-badge">
                            {Math.abs(goldDebt) > 0.0001 && (
                              <span className={`gram-debt-item gold`} style={{ color: goldDebt > 0 ? '#dc2626' : '#16a34a' }}>{goldDebt > 0 ? '+' : ''}{goldDebt.toFixed(3)} gr altın</span>
                            )}
                            {Math.abs(silverDebt) > 0.0001 && (
                              <span className={`gram-debt-item silver`} style={{ color: silverDebt > 0 ? '#dc2626' : '#16a34a' }}>{silverDebt > 0 ? '+' : ''}{silverDebt.toFixed(2)} gr gümüş</span>
                            )}
                          </span>
                        ) : '-'}
                      </span>
                    );
                  })()}
                  {visibleColumns.createdAt && (
                    <span className="col-date">{customer.created_at ? new Date(customer.created_at).toLocaleDateString('tr-TR') : '-'}</span>
                  )}
                  <span className="col-actions">
                    {user?.role === 'admin' && <button className="btn-icon" title="Düzenle" onClick={() => openEditModal(customer)}><Pencil size={16} /></button>}
                    <button className="btn-icon" title="Satış Geçmişi" onClick={() => fetchCustomerSales(customer)}><ClipboardList size={16} /></button>
                    {(debtSummary[customer.id] || creditSummary[customer.id]) && (
                      <>
                        <button className="btn-icon debt" title="Bakiye Detayı" onClick={() => fetchCustomerDebts(customer)}><Coins size={16} /></button>
                        <button className="btn-icon print-debt" title="Bakiye Raporu Yazdır" onClick={() => printDebtReport(customer)}><Printer size={16} /></button>
                      </>
                    )}
                    {user?.role === 'admin' && <button className="btn-icon delete" title="Sil" onClick={() => handleDeleteCustomer(customer)}><Trash2 size={16} /></button>}
                  </span>
                </div>
              ))}
            </div>
            {loadingMore && (
              <div className="loading-more">Yükleniyor...</div>
            )}
            {hasMore && <div ref={sentinelCallback.current} style={{ height: 1 }} />}
            {filteredCustomers.length > MAX_RENDERED_ROWS && (
              <div className="list-end">
                {filteredCustomers.length} sonuçtan ilk {MAX_RENDERED_ROWS} tanesi gösteriliyor — aramayı daraltın.
              </div>
            )}
            {!hasMore && filteredCustomers.length > 0 && filteredCustomers.length <= MAX_RENDERED_ROWS && (
              <div className="list-end">
                {searchTerm ? `${filteredCustomers.length} sonuç bulundu` : `Tüm müşteriler yüklendi (${customers.length})`}
              </div>
            )}
          </div>
        )}
          </>
        ) : (
          /* Shopify Müşterileri */
          <div className="shopify-customers-section">
            <div className="search-section">
              <div className="search-box">
                <span className="search-icon"><Search size={18} /></span>
                <input
                  type="text"
                  placeholder="Shopify müşteri ara (isim, email)..."
                  value={shopifySearchTerm}
                  onChange={(e) => {
                    setShopifySearchTerm(e.target.value);
                    if (e.target.value.length >= 2 || e.target.value.length === 0) {
                      fetchShopifyCustomers(e.target.value);
                    }
                  }}
                />
              </div>
            </div>

            {loadingShopifyCustomers ? (
              <div className="loading">Shopify müşterileri yükleniyor...</div>
            ) : (
              <div className="customers-list shopify-list">
                <div className="list-header">
                  <span className="col-name">Ad Soyad</span>
                  <span className="col-email">E-posta</span>
                  <span className="col-phone">Telefon</span>
                  <span className="col-orders">Sipariş Sayısı</span>
                  <span className="col-total">Toplam Harcama</span>
                  <span className="col-date">Kayıt Tarihi</span>
                </div>
                <div className="list-body">
                  {shopifyCustomers.length > 0 ? (
                    shopifyCustomers.map(customer => (
                      <div key={customer.id} className="list-row shopify-row">
                        <span className="col-name">
                          <span className="customer-initial shopify">{(customer.first_name || customer.email || '?').charAt(0).toUpperCase()}</span>
                          {customer.first_name} {customer.last_name}
                        </span>
                        <span className="col-email">{customer.email || '-'}</span>
                        <span className="col-phone">{formatPhone(customer.phone)}</span>
                        <span className="col-orders">{customer.orders_count || 0}</span>
                        <span className="col-total">
                          {customer.total_spent ? `$${Number(customer.total_spent).toLocaleString()}` : '-'}
                        </span>
                        <span className="col-date">
                          {customer.created_at ? new Date(customer.created_at).toLocaleDateString('tr-TR') : '-'}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      <ShoppingCart size={48} color="#cbd5e1" />
                      <p>Shopify müşterisi bulunamadı</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Yeni Müşteri Modal */}
      {showModal && (
        <div className={`modal-overlay ${closingModal === 'newCustomer' ? 'closing' : ''}`}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '95%', maxWidth: '750px' }}>
            <div className="modal-header">
              <h2><Plus size={20} style={{ marginRight: '8px' }} /> Yeni Müşteri Ekle</h2>
              <button className="close-btn" onClick={() => handleCloseModal('newCustomer', setShowModal, () => setExistingCustomerByPhone(null))}>✕</button>
            </div>
            <div className="modal-body" style={{ gap: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label><User size={16} style={{ marginRight: '6px' }} /> Ad Soyad *</label>
                  <input
                    type="text"
                    placeholder="Müşteri adı..."
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label><Phone size={16} style={{ marginRight: '6px' }} /> Telefon *</label>
                  <input
                    type="tel"
                    placeholder="0 532 XXX XX XX"
                    value={formatPhoneDisplay(newCustomer.phone)}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    required
                    style={existingCustomerByPhone ? { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.2)' } : {}}
                  />
                  {existingCustomerByPhone && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>⚠️</span> Bu numara <strong>{existingCustomerByPhone.name}</strong> müşterisine kayıtlı!
                    </div>
                  )}
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label><Mail size={16} style={{ marginRight: '6px' }} /> Email</label>
                  <input
                    type="email"
                    placeholder="email@example.com"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label><Store size={16} style={{ marginRight: '6px' }} /> Mağaza</label>
                  <select
                    value={newCustomer.store_type}
                    onChange={(e) => setNewCustomer({...newCustomer, store_type: e.target.value})}
                    className="form-select"
                  >
                    <option value="">Seçiniz...</option>
                    <option value="Altın">Altın</option>
                    <option value="Gümüş">Gümüş</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label><IdCard size={16} style={{ marginRight: '6px' }} /> Vergi No / TC</label>
                  <input
                    type="text"
                    placeholder="Vergi numarası veya TC"
                    value={newCustomer.tax_number}
                    onChange={(e) => setNewCustomer({...newCustomer, tax_number: e.target.value})}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label><CalendarDays size={16} style={{ marginRight: '6px' }} /> Doğum Tarihi</label>
                  <input
                    type="date"
                    value={newCustomer.birthday}
                    onChange={(e) => setNewCustomer({...newCustomer, birthday: e.target.value})}
                  />
                </div>
                <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                  <label><MapPin size={16} style={{ marginRight: '6px' }} /> Adres</label>
                  <input
                    type="text"
                    placeholder="Adres bilgisi..."
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                  />
                </div>
              </div>
              {/* KVKK Onay Checkbox */}
              <div className="consent-section">
                <div className="consent-checkbox kvkk-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newCustomer.kvkk_consent}
                      onChange={(e) => setNewCustomer({...newCustomer, kvkk_consent: e.target.checked})}
                    />
                    <span className="checkbox-text">
                      <span className="kvkk-link" onClick={(e) => { e.preventDefault(); setShowKvkkModal(true); }}>KVKK Aydınlatma Metni</span>'ni okudum ve kabul ediyorum *
                    </span>
                  </label>
                  {!newCustomer.kvkk_consent && (
                    <span className="kvkk-warning">KVKK onayı zorunludur</span>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowModal(false); setExistingCustomerByPhone(null); }}>İptal</button>
              <button className="btn-save" onClick={handleAddCustomer} disabled={!!existingCustomerByPhone || !newCustomer.kvkk_consent}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Müşteri Düzenleme Modal */}
      {showEditModal && editingCustomer && (
        <div className={`modal-overlay ${closingModal === 'editCustomer' ? 'closing' : ''}`}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Pencil size={20} style={{ marginRight: '8px' }} /> Müşteri Düzenle</h2>
              <button className="close-btn" onClick={() => handleCloseModal('editCustomer', setShowEditModal)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label><User size={16} style={{ marginRight: '6px' }} /> Ad Soyad *</label>
                <input
                  type="text"
                  placeholder="Müşteri adı..."
                  value={editingCustomer.name}
                  onChange={(e) => setEditingCustomer({...editingCustomer, name: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label><Store size={16} style={{ marginRight: '6px' }} /> Mağaza</label>
                <select
                  value={editingCustomer.store_type || ''}
                  onChange={(e) => setEditingCustomer({...editingCustomer, store_type: e.target.value})}
                  className="form-select"
                >
                  <option value="">Seçiniz...</option>
                  <option value="Altın">Altın</option>
                  <option value="Gümüş">Gümüş</option>
                </select>
              </div>
              <div className="form-group">
                <label><Phone size={16} style={{ marginRight: '6px' }} /> Telefon</label>
                <input
                  type="tel"
                  placeholder="0 532 XXX XX XX"
                  value={formatPhoneDisplay(editingCustomer.phone)}
                  onChange={(e) => setEditingCustomer({...editingCustomer, phone: normalizePhoneDigits(e.target.value)})}
                />
              </div>
              <div className="form-group">
                <label><Mail size={16} style={{ marginRight: '6px' }} /> Email</label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={editingCustomer.email}
                  onChange={(e) => setEditingCustomer({...editingCustomer, email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label><MapPin size={16} style={{ marginRight: '6px' }} /> Adres</label>
                <textarea
                  placeholder="Adres bilgisi..."
                  value={editingCustomer.address}
                  onChange={(e) => setEditingCustomer({...editingCustomer, address: e.target.value})}
                ></textarea>
              </div>
              <div className="form-group">
                <label><IdCard size={16} style={{ marginRight: '6px' }} /> Vergi No / TC</label>
                <input
                  type="text"
                  placeholder="Vergi numarası veya TC"
                  value={editingCustomer.tax_number}
                  onChange={(e) => setEditingCustomer({...editingCustomer, tax_number: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label><CalendarDays size={16} style={{ marginRight: '6px' }} /> Doğum Tarihi</label>
                <input
                  type="date"
                  value={editingCustomer.birthday}
                  onChange={(e) => setEditingCustomer({...editingCustomer, birthday: e.target.value})}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowEditModal(false)}>İptal</button>
              <button className="btn-save" onClick={handleUpdateCustomer}>Güncelle</button>
            </div>
          </div>
        </div>
      )}

      {/* Satış ve Tahsilat Geçmişi Modal */}
      {showSalesModal && (
        <div className={`modal-overlay ${closingModal === 'sales' ? 'closing' : ''}`}>
          <div className="modal large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><ClipboardList size={20} style={{ marginRight: '8px' }} /> {selectedCustomer?.name} - Müşteri Geçmişi</h2>
              <button className="close-btn" onClick={() => handleCloseModal('sales', setShowSalesModal)}>✕</button>
            </div>
            {/* Sekme Navigasyonu */}
            <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e5e7eb', padding: '0 20px', background: '#f8fafc' }}>
              <button onClick={() => setSalesModalTab('sales')} style={{ padding: '10px 18px', fontWeight: 600, fontSize: '0.82rem', border: 'none', background: 'none', cursor: 'pointer', borderBottom: salesModalTab === 'sales' ? '2px solid #2563eb' : '2px solid transparent', color: salesModalTab === 'sales' ? '#2563eb' : '#64748b', marginBottom: '-2px' }}>
                <ShoppingCart size={14} style={{ marginRight: '5px', verticalAlign: 'middle' }} />İşlem Geçmişi ({customerSales.length})
              </button>
              <button onClick={() => setSalesModalTab('balance')} style={{ padding: '10px 18px', fontWeight: 600, fontSize: '0.82rem', border: 'none', background: 'none', cursor: 'pointer', borderBottom: salesModalTab === 'balance' ? '2px solid #2563eb' : '2px solid transparent', color: salesModalTab === 'balance' ? '#2563eb' : '#64748b', marginBottom: '-2px' }}>
                <Coins size={14} style={{ marginRight: '5px', verticalAlign: 'middle' }} />TL Bakiye ({balanceHistory.length})
              </button>
              {gramBalanceHistory.length > 0 && (
                <button onClick={() => setSalesModalTab('gram_balance')} style={{ padding: '10px 18px', fontWeight: 600, fontSize: '0.82rem', border: 'none', background: 'none', cursor: 'pointer', borderBottom: salesModalTab === 'gram_balance' ? '2px solid #d97706' : '2px solid transparent', color: salesModalTab === 'gram_balance' ? '#d97706' : '#64748b', marginBottom: '-2px' }}>
                  <Scale size={14} style={{ marginRight: '5px', verticalAlign: 'middle' }} />Gram Bakiye ({gramBalanceHistory.length})
                </button>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '4px' }}>
                {customerBalance !== 0 && (
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: customerBalance > 0 ? '#dc2626' : '#16a34a' }}>
                    {customerBalance > 0 ? `₺${customerBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} Borç` : `₺${Math.abs(customerBalance).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} Alacak`}
                  </span>
                )}
                {(gramBalance !== 0 || gumusBalance !== 0) && (
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#d97706' }}>
                    {gramBalance !== 0 && `${gramBalance > 0 ? '+' : ''}${gramBalance.toFixed(3)} gr altın`}
                    {gramBalance !== 0 && gumusBalance !== 0 && ' | '}
                    {gumusBalance !== 0 && `${gumusBalance > 0 ? '+' : ''}${gumusBalance.toFixed(2)} gr gümüş`}
                  </span>
                )}
              </div>
            </div>
            <div className="modal-body">
              {salesModalTab === 'balance' ? (
                /* ========== BAKİYE GEÇMİŞİ SEKMESİ ========== */
                balanceHistory.length === 0 ? (
                  <div className="empty-state">
                    <span><Coins size={48} /></span>
                    <p>Bu müşterinin bakiye geçmişi bulunmuyor</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: '#f1f5f9' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Tarih</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>İşlem</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Açıklama</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Önceki Bakiye</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>İşlem Tutarı</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Yeni Bakiye</th>
                        </tr>
                      </thead>
                      <tbody>
                        {balanceHistory.map((tx, idx) => {
                          const amountTl = parseFloat(tx.amount_tl) || 0;
                          const typeLabels = { sale: 'Satış', collection: 'Tahsilat', refund: 'İade', manual: (tx.description || '').includes('Tahsilat iptali') ? 'Tahsilat İptali' : (tx.description || '').includes('Bakiye kullanımı') ? 'Bakiye Kullanımı' : 'Manuel', migration: 'Aktarım', exchange: 'Değişim', sale_delete: 'Fiş Silindi' };
                          return (
                            <tr key={tx.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '7px 12px', whiteSpace: 'nowrap', color: '#64748b' }}>{new Date(tx.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })} {new Date(tx.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</td>
                              <td style={{ padding: '7px 12px' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: amountTl > 0 ? '#fef2f2' : '#f0fdf4', color: amountTl > 0 ? '#dc2626' : '#16a34a' }}>
                                  {typeLabels[tx.transaction_type] || tx.transaction_type}
                                </span>
                              </td>
                              <td style={{ padding: '7px 12px', color: '#334155', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.description || '-'}</td>
                              <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 500, color: '#64748b' }}>₺{tx.prev_balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                              <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: amountTl > 0 ? '#dc2626' : '#16a34a' }}>{amountTl > 0 ? '+' : ''}₺{amountTl.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                              <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: tx.new_balance > 0 ? '#dc2626' : tx.new_balance < 0 ? '#16a34a' : '#64748b' }}>₺{tx.new_balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              ) : salesModalTab === 'gram_balance' ? (
                /* ========== GRAM BAKİYE GEÇMİŞİ SEKMESİ ========== */
                gramBalanceHistory.length === 0 ? (
                  <div className="empty-state">
                    <span><Scale size={48} /></span>
                    <p>Bu müşterinin gram bakiye geçmişi bulunmuyor</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: '#fffbeb' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#92400e', borderBottom: '2px solid #fde68a' }}>Tarih</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#92400e', borderBottom: '2px solid #fde68a' }}>İşlem</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#92400e', borderBottom: '2px solid #fde68a' }}>Tür</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#92400e', borderBottom: '2px solid #fde68a' }}>Açıklama</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#92400e', borderBottom: '2px solid #fde68a' }}>İşlem (gr)</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#92400e', borderBottom: '2px solid #fde68a' }}>TL Karşılığı</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#92400e', borderBottom: '2px solid #fde68a' }}>Bakiye (gr)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gramBalanceHistory.map((tx, idx) => {
                          const t = tx.debt_type || tx.currency_type;
                          const isGold = t === 'GRAM';
                          const amt = parseFloat(tx.amount) || 0;
                          const amtTl = parseFloat(tx.amount_tl) || 0;
                          const newBal = isGold ? tx.new_gram : tx.new_gumus;
                          const typeLabels = { sale: 'Satış', collection: 'Tahsilat', refund: 'İade', manual: (tx.description || '').includes('Tahsilat iptali') ? 'Tahsilat İptali' : 'Manuel', migration: 'Aktarım', exchange: 'Değişim', sale_delete: 'Fiş Silindi' };
                          return (
                            <tr key={tx.id || idx} style={{ borderBottom: '1px solid #fef3c7' }}>
                              <td style={{ padding: '7px 12px', whiteSpace: 'nowrap', color: '#64748b' }}>{new Date(tx.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })} {new Date(tx.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</td>
                              <td style={{ padding: '7px 12px' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: amt > 0 ? '#fef2f2' : '#f0fdf4', color: amt > 0 ? '#dc2626' : '#16a34a' }}>
                                  {typeLabels[tx.transaction_type] || tx.transaction_type}
                                </span>
                              </td>
                              <td style={{ padding: '7px 12px' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: isGold ? '#fffbeb' : '#f0f9ff', color: isGold ? '#b45309' : '#0369a1' }}>
                                  {isGold ? 'Altın' : 'Gümüş'}
                                </span>
                              </td>
                              <td style={{ padding: '7px 12px', color: '#334155', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.description || '-'}</td>
                              <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: amt > 0 ? '#dc2626' : '#16a34a' }}>{amt > 0 ? '+' : ''}{amt.toFixed(isGold ? 3 : 2)} gr</td>
                              <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 500, color: amtTl > 0 ? '#dc2626' : amtTl < 0 ? '#16a34a' : '#64748b' }}>{amtTl > 0 ? '+' : ''}{amtTl < 0 ? '-' : ''}₺{Math.abs(amtTl).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                              <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: newBal > 0.001 ? '#dc2626' : newBal < -0.001 ? '#16a34a' : '#64748b' }}>{newBal > 0 ? '+' : ''}{newBal.toFixed(isGold ? 3 : 2)} gr</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (loadingSales || loadingPayments) ? (
                /* ========== İŞLEM GEÇMİŞİ SEKMESİ ========== */
                <div className="loading">Yükleniyor...</div>
              ) : mergedHistory.length === 0 ? (
                <div className="empty-state">
                  <span><ClipboardList size={48} /></span>
                  <p>Bu müşterinin henüz işlem kaydı yok</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {mergedHistory.map(entry => {
                    const isExpanded = expandedHistoryItems.has(entry._key);

                    if (entry._type === 'sale') {
                      const sale = entry;

                      // Hurda kaydı
                      if (sale.is_scrap) {
                        const scrapTotal = Number(sale.total) || 0;
                        return (
                          <div key={entry._key}>
                            <div
                              className="all-sales-item"
                              style={{ cursor: 'pointer', borderLeft: `4px solid ${sale.is_refunded ? '#94a3b8' : '#d97706'}` }}
                              onClick={() => toggleHistoryItem(entry._key)}
                            >
                              <div className="all-sales-item-left">
                                <div className="all-sales-item-header">
                                  {sale.store_scrap_number && <span className="all-sales-item-no">#{sale.store_scrap_number}</span>}
                                  <span style={{ backgroundColor: '#d97706', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', marginLeft: sale.store_scrap_number ? '6px' : 0 }}>HURDA</span>
                                  {sale.is_refunded ? (
                                    <span style={{ backgroundColor: '#94a3b8', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', marginLeft: '4px' }}>İADE EDİLDİ</span>
                                  ) : null}
                                  {sale.store_name && (
                                    <span className="all-sales-item-store" style={{ marginLeft: '4px' }}>{sale.store_name}</span>
                                  )}
                                </div>
                                <div className="all-sales-item-details">
                                  <span className="all-sales-item-date">
                                    <Calendar size={14} />
                                    {sale._date.toLocaleDateString('tr-TR')} {sale._date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  {sale.items && <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>({sale.items.length} kalem)</span>}
                                  {sale.staff_name && (
                                    <span style={{ fontSize: '11px', color: '#6366f1', backgroundColor: '#eef2ff', padding: '1px 6px', borderRadius: '4px', fontWeight: '500' }}>
                                      {sale.staff_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div className="all-sales-item-right">
                                  <span className="all-sales-item-total" style={{ color: sale.is_refunded ? '#94a3b8' : '#d97706', textDecoration: sale.is_refunded ? 'line-through' : 'none' }}>
                                    ₺{scrapTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                  </span>
                                  <span className="all-sales-item-method">{sale.payment_method || '-'}</span>
                                </div>
                                <button
                                  title="Hurda Sayfasında Aç"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/scrap-purchase?scrap=${sale.scrap_id}`);
                                  }}
                                  style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: '36px', height: '36px', borderRadius: '8px',
                                    border: '1px solid #e2e8f0', background: '#f8fafc',
                                    cursor: 'pointer', color: '#d97706', flexShrink: 0,
                                    transition: 'all 0.15s'
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#d97706'; e.currentTarget.style.color = '#fff'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#d97706'; }}
                                >
                                  <ExternalLink size={16} />
                                </button>
                              </div>
                            </div>
                            {isExpanded && sale.items && (
                              <div style={{ padding: '10px 14px 12px 18px', background: '#fffbeb', borderRadius: '0 0 8px 8px', border: '1px solid #fde68a', borderTop: 'none' }}>
                                {sale.items.map((item, idx) => (
                                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.82rem', borderBottom: idx < sale.items.length - 1 ? '1px solid #fef3c7' : 'none' }}>
                                    <span style={{ color: '#92400e' }}>{item.product_name}</span>
                                    <div style={{ display: 'flex', gap: '12px', color: '#78350f', fontWeight: 500 }}>
                                      {item.weight && <span>{Number(item.weight).toFixed(3)} gr</span>}
                                      <span>₺{Number(item.total_price).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Tamirat ödemesi kaydı
                      if (sale.is_repair) {
                        const repairTotal = Number(sale.total) || 0;
                        const repairPaymentMethodText = (() => {
                          if (sale.payment_method === 'Karışık' && sale.payment_details) {
                            try {
                              const details = typeof sale.payment_details === 'string' ? JSON.parse(sale.payment_details) : sale.payment_details;
                              if (Array.isArray(details) && details.length > 0) {
                                return details.map(d => d.method).join(' + ');
                              }
                            } catch (e) {}
                          }
                          return sale.payment_method || '-';
                        })();
                        return (
                          <div key={entry._key}>
                            <div
                              className="all-sales-item"
                              style={{ cursor: 'pointer', borderLeft: '4px solid #059669' }}
                              onClick={() => toggleHistoryItem(entry._key)}
                            >
                              <div className="all-sales-item-left">
                                <div className="all-sales-item-header">
                                  {sale.repair_code && <span className="all-sales-item-no">#{sale.repair_code}</span>}
                                  <span style={{ backgroundColor: '#059669', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', marginLeft: sale.repair_code ? '6px' : 0 }}>TAMİRAT</span>
                                  {sale.store_name && (
                                    <span className="all-sales-item-store" style={{ marginLeft: '4px' }}>{sale.store_name}</span>
                                  )}
                                </div>
                                <div className="all-sales-item-details">
                                  <span className="all-sales-item-date">
                                    <Calendar size={14} />
                                    {sale._date.toLocaleDateString('tr-TR')} {sale._date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  {sale.staff_name && (
                                    <span style={{ fontSize: '11px', color: '#6366f1', backgroundColor: '#eef2ff', padding: '1px 6px', borderRadius: '4px', fontWeight: '500' }}>
                                      {sale.staff_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div className="all-sales-item-right">
                                  <span className="all-sales-item-total" style={{ color: '#059669' }}>
                                    ₺{repairTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                  </span>
                                  <span className="all-sales-item-method">{repairPaymentMethodText}</span>
                                </div>
                                <button
                                  title="Tamirat Sayfasında Aç"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/repairs', { state: { openRepairId: sale.repair_id } });
                                  }}
                                  style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: '36px', height: '36px', borderRadius: '8px',
                                    border: '1px solid #e2e8f0', background: '#f8fafc',
                                    cursor: 'pointer', color: '#059669', flexShrink: 0,
                                    transition: 'all 0.15s'
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#059669'; e.currentTarget.style.color = '#fff'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#059669'; }}
                                >
                                  <ExternalLink size={16} />
                                </button>
                              </div>
                            </div>
                            {isExpanded && (
                              <div style={{ padding: '10px 14px 12px 18px', background: '#ecfdf5', borderRadius: '0 0 8px 8px', border: '1px solid #a7f3d0', borderTop: 'none' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.82rem' }}>
                                  <span style={{ color: '#065f46' }}>{sale.item_description || 'Tamirat Ödemesi'}</span>
                                  <span style={{ color: '#065f46', fontWeight: 500 }}>₺{repairTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      const isRefund = sale.sale_type === 'refund' || sale.status === 'refunded';
                      const total = Number(sale.total) || 0;
                      const subtotal = Number(sale.subtotal) || 0;
                      const discount = Number(sale.discount) || 0;
                      const scrapAmount = Number(sale.scrap_total_amount) || 0;
                      const scrapDebt = Number(sale.scrap_debt_discount) || 0;
                      let saleAmount;
                      if (discount > 0 && subtotal > 0 && Math.abs(total - subtotal) < 1) {
                        saleAmount = total - discount - scrapDebt;
                      } else {
                        saleAmount = total + scrapAmount;
                      }
                      const paymentMethodText = (() => {
                        if (sale.payment_method === 'Karışık' && sale.payment_details) {
                          try {
                            const details = typeof sale.payment_details === 'string' ? JSON.parse(sale.payment_details) : sale.payment_details;
                            if (Array.isArray(details) && details.length > 0) {
                              return details.map(d => d.method).join(' + ');
                            }
                          } catch (e) {}
                        }
                        return sale.payment_method || '-';
                      })();

                      return (
                        <div key={entry._key}>
                          <div
                            className={`all-sales-item ${isRefund ? 'exchange-refund-item' : ''}`}
                            style={{ cursor: 'pointer', borderLeft: isRefund ? '4px solid #ef4444' : undefined }}
                            onClick={() => !sale.is_exchange && openSaleDetailInModal(sale.id)}
                          >
                            <div className="all-sales-item-left">
                              <div className="all-sales-item-header">
                                <span className="all-sales-item-no">#{sale.receipt_number || sale.store_sale_number || sale.id}</span>
                                {isRefund && (
                                  <span style={{ backgroundColor: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', marginLeft: '6px' }}>İADE</span>
                                )}
                                {sale.is_exchange && (
                                  <span style={{ backgroundColor: '#f59e0b', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', marginLeft: '6px' }}>DEĞİŞİM</span>
                                )}
                                <span className={`all-sales-item-store store-${sale.store_id}`}>
                                  {sale.store_id === 1 ? 'Akik Gold Jewellery' : sale.store_id === 2 ? 'Akik Gümüş Osmangazi' : sale.store_id === 3 ? 'Akik Gümüş Yeşilsu' : ''}
                                </span>
                              </div>
                              <div className="all-sales-item-details">
                                <span className="all-sales-item-date">
                                  <Calendar size={14} />
                                  {sale._date.toLocaleDateString('tr-TR')} {sale._date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {sale.items && <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>({sale.items.length} ürün)</span>}
                                {sale.staff_name && (
                                  <span style={{ fontSize: '11px', color: '#6366f1', backgroundColor: '#eef2ff', padding: '1px 6px', borderRadius: '4px', fontWeight: '500' }}>
                                    {sale.staff_name}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div className="all-sales-item-right">
                                <span className="all-sales-item-total" style={isRefund ? { color: '#dc2626' } : {}}>
                                  {isRefund ? `-₺${Math.abs(saleAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : `₺${saleAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
                                </span>
                                <span className="all-sales-item-method">{paymentMethodText}</span>
                              </div>
                              <button
                                title="Perakende Satışta Aç"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (sale.is_exchange && sale.original_sale_id) {
                                    navigate(`/retail-sales?sale=${sale.original_sale_id}`);
                                  } else {
                                    navigate(`/retail-sales?sale=${sale.id}`);
                                  }
                                }}
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  width: '36px', height: '36px', borderRadius: '8px',
                                  border: '1px solid #e2e8f0', background: '#f8fafc',
                                  cursor: 'pointer', color: '#2563eb', flexShrink: 0,
                                  transition: 'all 0.15s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#2563eb'; }}
                              >
                                <ExternalLink size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // Tahsilat entry - satış ile aynı tasarımda
                    const payment = entry;
                    const paymentMethodText = (() => {
                      if (payment.payment_details) {
                        try {
                          const details = typeof payment.payment_details === 'string' ? JSON.parse(payment.payment_details) : payment.payment_details;
                          if (Array.isArray(details) && details.length > 0) {
                            return details.map(d => {
                              let methodText = d.method || payment.payment_method;
                              if (d.bankAccount) methodText += ` (${stripBankAccountType(d.bankAccount)})`;
                              return methodText;
                            }).join(' + ');
                          }
                        } catch (e) {}
                      }
                      return payment.payment_method;
                    })();

                    return (
                      <div key={entry._key}>
                        <div
                          className="all-sales-item"
                          style={{ cursor: 'pointer', borderLeft: '4px solid #0f766e' }}
                          onClick={() => toggleHistoryItem(entry._key)}
                        >
                          <div className="all-sales-item-left">
                            <div className="all-sales-item-header">
                              {payment.receipt_number && <span className="all-sales-item-no">#{payment.receipt_number}</span>}
                              <span style={{ backgroundColor: '#0f766e', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', marginLeft: payment.receipt_number ? '6px' : 0 }}>TAHSİLAT</span>
                              <span style={{
                                background: payment.debt_type === 'TL' ? '#e0f2fe' : payment.debt_type === 'GRAM' ? '#fef3c7' : '#f3e8ff',
                                color: payment.debt_type === 'TL' ? '#0369a1' : payment.debt_type === 'GRAM' ? '#b45309' : '#7c3aed',
                                padding: '2px 6px', borderRadius: '6px', fontSize: '0.65rem', fontWeight: 500, marginLeft: '4px'
                              }}>
                                {payment.debt_type === 'TL' ? 'TL' : payment.debt_type === 'GRAM' ? 'Altın' : 'Gümüş'}
                              </span>
                              {payment.store_name && (
                                <span className={`all-sales-item-store`} style={{ marginLeft: '4px' }}>
                                  {payment.store_name}
                                </span>
                              )}
                            </div>
                            <div className="all-sales-item-details">
                              <span className="all-sales-item-date">
                                <Calendar size={14} />
                                {payment._date.toLocaleDateString('tr-TR')} {payment._date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {payment.staff_name && (
                                <span style={{ fontSize: '11px', color: '#6366f1', backgroundColor: '#eef2ff', padding: '1px 6px', borderRadius: '4px', fontWeight: '500' }}>
                                  {payment.staff_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div className="all-sales-item-right">
                              <span className="all-sales-item-total" style={{ color: '#0f766e' }}>
                                ₺{Number(payment.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                {payment.gram_amount && (payment.debt_type === 'GRAM' || payment.debt_type === 'GUMUS') && (
                                  <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 400, marginLeft: '4px' }}>
                                    ({Number(payment.gram_amount).toFixed(payment.debt_type === 'GUMUS' ? 2 : 3)} gr)
                                  </span>
                                )}
                              </span>
                              <span className="all-sales-item-method">{paymentMethodText}</span>
                            </div>
                            <button
                              title="Tahsilat Fişini Aç"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/retail-sales?payment=${payment.id}`);
                              }}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '36px', height: '36px', borderRadius: '8px',
                                border: '1px solid #e2e8f0', background: '#f8fafc',
                                cursor: 'pointer', color: '#2563eb', flexShrink: 0,
                                transition: 'all 0.15s'
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#2563eb'; }}
                            >
                              <ExternalLink size={16} />
                            </button>
                          </div>
                        </div>
                        {/* Tahsilat Detayı - Açılır */}
                        {isExpanded && (
                          <div style={{ padding: '10px 14px 12px 18px', borderTop: '1px solid #e5e7eb', background: '#fafbfc', borderRadius: '0 0 8px 8px', marginTop: '-1px', border: '1px solid #e2e8f0', borderTop: 'none' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '0.85rem' }}>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#64748b', fontSize: '0.65rem' }}>Önceki Borç</div>
                                <div style={{ fontWeight: 500, color: '#dc2626' }}>₺{Number(payment.previous_debt || payment.debt_amount_tl || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>
                              </div>
                              <span style={{ color: '#94a3b8' }}>→</span>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#64748b', fontSize: '0.65rem' }}>Ödeme</div>
                                <div style={{ fontWeight: 600, color: '#0f766e' }}>₺{Number(payment.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>
                              </div>
                              <span style={{ color: '#94a3b8' }}>→</span>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#64748b', fontSize: '0.65rem' }}>Kalan Borç</div>
                                <div style={{ fontWeight: 500, color: (payment.remaining_debt || 0) > 0 ? '#f59e0b' : '#22c55e' }}>
                                  {(payment.remaining_debt || 0) > 0
                                    ? `₺${Number(payment.remaining_debt).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                                    : 'Ödendi ✓'
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Dip Toplam - tek satır */}
                  <div style={{
                    padding: '10px 14px',
                    background: '#f1f5f9',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    flexWrap: 'wrap',
                    fontSize: '0.82rem'
                  }}>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>Alışveriş: <span style={{ fontWeight: 700 }}>₺{historySummary.totalSales.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span></span>
                    {historySummary.totalRefunds > 0 && (
                      <>
                        <span style={{ color: '#cbd5e1' }}>|</span>
                        <span style={{ fontWeight: 600, color: '#dc2626' }}>İade: -₺{historySummary.totalRefunds.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                        <span style={{ color: '#cbd5e1' }}>|</span>
                        <span style={{ fontWeight: 600, color: '#334155' }}>Net: ₺{(historySummary.totalSales - historySummary.totalRefunds).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                      </>
                    )}
                    <span style={{ color: '#cbd5e1' }}>|</span>
                    <span style={{ fontWeight: 600, color: '#0f766e' }}>Tahsilat: ₺{historySummary.totalPayments.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                    <span style={{ color: '#cbd5e1' }}>|</span>
                    <span style={{ fontWeight: 700, color: historySummary.netBalance > 0 ? '#dc2626' : historySummary.netBalance < 0 ? '#22c55e' : '#64748b' }}>
                      Bakiye: {historySummary.netBalance > 0
                        ? `₺${historySummary.netBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} Borç`
                        : historySummary.netBalance < 0
                          ? `₺${Math.abs(historySummary.netBalance).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} Alacak`
                          : 'Borç Yok'
                      }
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Borç Detay Modal */}
      {showDebtModal && (
        <div className={`modal-overlay ${closingModal === 'debt' ? 'closing' : ''}`}>
          <div className="modal large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Coins size={20} style={{ marginRight: '8px' }} /> {selectedCustomer?.name} - Bakiye Detayı</h2>
              <button className="close-btn" onClick={() => handleCloseModal('debt', setShowDebtModal)}>✕</button>
            </div>
            <div className="modal-body">
              {loadingDebts ? (
                <div className="loading">Yükleniyor...</div>
              ) : customerDebts.length === 0 ? (
                <div className="empty-state">
                  <span><CheckCircle size={48} /></span>
                  <p>Bu müşterinin borcu bulunmuyor</p>
                </div>
              ) : (
                <div className="debts-list">
                  {groupedCustomerDebts.map(group => {
                    // Grup için güncel toplam TL değerini hesapla
                    const groupCurrentValueTL = group.debts.reduce((sum, debt) => {
                      return sum + calculateCurrentValue(debt);
                    }, 0);

                    return (
                    <div key={group.debt_type} className="debt-card grouped-debt-card">
                      <div className="debt-header" style={{ cursor: 'pointer' }} onClick={() => setSelectedDebtGroup(selectedDebtGroup === group.debt_type ? null : group.debt_type)}>
                        <div className="debt-info">
                          <span className={`debt-type ${group.debt_type === 'GRAM' ? 'gram' : group.debt_type === 'GUMUS' ? 'gumus' : 'tl'}`}>
                            {group.debt_type === 'GRAM' ? 'Altın' : group.debt_type === 'GUMUS' ? 'Gümüş' : 'TL'}
                          </span>
                          <span className="debt-count" style={{ fontSize: '0.85rem', color: '#6b7280', marginLeft: '8px' }}>
                            ({group.debts.length} kayıt)
                          </span>
                          <span style={{ marginLeft: 'auto', marginRight: '8px' }}>
                            {selectedDebtGroup === group.debt_type ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          </span>
                        </div>
                        <div className="debt-amount">
                          {(group.debt_type === 'GRAM' || group.debt_type === 'GUMUS') ? (
                            <>
                              <span className="gram-amount" style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{group.total_amount.toFixed(group.debt_type === 'GUMUS' ? 2 : 3)} gr</span>
                              <span className="tl-amount current-value" style={{ display: 'block', fontSize: '0.9rem' }}>= ₺{groupCurrentValueTL.toLocaleString('tr-TR', {maximumFractionDigits: 2})}</span>
                            </>
                          ) : (
                            <span className="tl-amount" style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>₺{group.total_amount_tl.toLocaleString('tr-TR', {maximumFractionDigits: 2})}</span>
                          )}
                        </div>
                      </div>

                      {/* Genişletilmiş borç detayları */}
                      {selectedDebtGroup === group.debt_type && (
                        <div className="debt-group-details" style={{ borderTop: '1px solid #e5e7eb', marginTop: '12px', paddingTop: '12px' }}>
                          {group.debts.map(debt => {
                            const currentValue = calculateCurrentValue(debt);
                            return (
                              <div key={debt.id} className="debt-detail-item" style={{ padding: '10px', marginBottom: '8px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span className="debt-date" style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                                    {new Date(debt.created_at).toLocaleDateString('tr-TR')}
                                  </span>
                                  <div className="debt-amount">
                                    {(debt.debt_type === 'GRAM' || debt.debt_type === 'GUMUS') ? (
                                      <>
                                        <span className="gram-amount">{Number(debt.amount).toFixed(debt.debt_type === 'GUMUS' ? 2 : 3)} gr</span>
                                        <span className="tl-amount current-value" style={{ marginLeft: '8px' }}>= ₺{currentValue.toLocaleString('tr-TR', {maximumFractionDigits: 2})}</span>
                                      </>
                                    ) : (
                                      <span className="tl-amount">₺{Number(debt.amount_tl).toLocaleString()}</span>
                                    )}
                                  </div>
                                </div>
                                {debt.description && (
                                  <div className="debt-description" style={{ marginTop: '6px' }}>
                                    <small style={{ color: '#6b7280' }}>{debt.description}</small>
                                  </div>
                                )}
                                {debt.sale_items && debt.sale_items.length > 0 && (
                                  <div className="debt-items" style={{ marginTop: '8px', fontSize: '0.85rem' }}>
                                    {debt.sale_items.map((item, idx) => (
                                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280' }}>
                                        <span>{item.product_name}</span>
                                        <span>{item.quantity} ad - ₺{Number(item.total_price).toLocaleString()}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div style={{ marginTop: '8px', textAlign: 'right' }}>
                                  <button
                                    className="btn-paid"
                                    style={{ padding: '4px 12px', fontSize: '0.85rem' }}
                                    onClick={(e) => { e.stopPropagation(); navigate(`/retail-sales?collection_customer_id=${selectedCustomer.id}`); }}
                                  >
                                    Tahsilat Yap
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="debt-actions" style={{ marginTop: '12px' }}>
                        <button
                          className="btn-paid"
                          onClick={() => navigate(`/retail-sales?collection_customer_id=${selectedCustomer.id}`)}
                        >
                          Tahsilat Yap ({group.debt_type === 'GRAM' ? 'Altın' : group.debt_type === 'GUMUS' ? 'Gümüş' : 'TL'})
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ödeme Modal */}
      {showPaymentModal && selectedDebt && (
        <div className={`modal-overlay ${closingModal === 'payment' ? 'closing' : ''}`}>
          <div className="modal payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ödeme Al</h2>
              <button className="close-btn" onClick={() => handleCloseModal('payment', setShowPaymentModal, () => setScrapPaymentData(prev => ({...prev, custom_purity_rate: null})))}>✕</button>
            </div>
            <div className="modal-body">
              <div className="payment-info">
                {(selectedDebt.debt_type === 'GRAM' || selectedDebt.debt_type === 'GUMUS') ? (
                  <div className={`payment-info-row ${selectedDebt.debt_type === 'GRAM' ? 'gram-highlight' : 'silver-highlight'}`}>
                    <span>{selectedDebt.debt_type === 'GRAM' ? 'Altın Borcu' : 'Gümüş Borcu'}:</span>
                    <strong>{Number(selectedDebt.amount).toFixed(selectedDebt.debt_type === 'GUMUS' ? 2 : 3)} gr</strong>
                  </div>
                ) : (
                  <div className="payment-info-row">
                    <span>Borç Tutarı:</span>
                    <strong>₺{Number(selectedDebt.amount_tl).toLocaleString()}</strong>
                  </div>
                )}
              </div>

              {/* Gram borçlar için kur bilgisi */}
              {(selectedDebt.debt_type === 'GRAM' || selectedDebt.debt_type === 'GUMUS') && (
                <div className="margin-rate-section">
                  <div className="rate-info-row">
                    <span>Anlık {selectedDebt.debt_type === 'GRAM' ? 'Altın' : 'Gümüş'} Satış:</span>
                    <strong>₺{(selectedDebt.debt_type === 'GRAM' ? prices?.altin_satis : prices?.gumus_satis) || '0'}</strong>
                  </div>
                  {getMarginPercentage() !== null && (
                    <div className="rate-info-row margin-info">
                      <span>Makas Farkı:</span>
                      <strong>+%{getMarginPercentage()}</strong>
                    </div>
                  )}
                  <div className="calculated-rate" style={{ marginTop: '8px', padding: '10px', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderRadius: '8px' }}>
                    <span>Uygulanan Kur:</span>
                    <strong style={{ fontSize: '1.1rem', color: '#d97706' }}>₺{calculateMarginRate().toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                  </div>
                </div>
              )}

              {/* Alınan Ödemeler - kompakt chip */}
              {mixedPayments.length > 0 && (
                <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                  {mixedPayments.map(payment => (
                    <div key={payment.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', fontSize: '0.78rem' }}>
                      <span style={{ fontWeight: 600, color: '#334155' }}>{payment.method}</span>
                      <span style={{ color: '#16a34a', fontWeight: 700 }}>
                        {payment.gramAmount
                          ? `${payment.gramAmount.toFixed(payment.debtType === 'GUMUS' ? 2 : 3)} gr`
                          : `₺${payment.amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})}`}
                      </span>
                      <button onClick={() => removePayment(payment.id)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0 0 0 2px', fontSize: '13px', lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                  <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '4px' }}>
                    Toplam: <strong style={{ color: '#16a34a' }}>₺{getPaymentsTotal().toLocaleString('tr-TR', {minimumFractionDigits: 2})}</strong>
                    {getRemainingDebt() > 0.01 && (
                      <> · Kalan: <strong style={{ color: '#f59e0b' }}>₺{getRemainingDebt().toLocaleString('tr-TR', {minimumFractionDigits: 2})}</strong></>
                    )}
                    {getRemainingDebt() <= 0.01 && <> · <strong style={{ color: '#16a34a' }}>Tamamlandı</strong></>}
                  </span>
                </div>
              )}

              {!scrapPaymentMode && getRemainingDebt() > 0.01 && (
                <div className="form-group">
                  {/* Altın/Gümüş borcu için TL/Gram seçeneği */}
                  {(selectedDebt.debt_type === 'GRAM' || selectedDebt.debt_type === 'GUMUS') && (
                    <div className="payment-input-mode-toggle" style={{ marginBottom: '10px' }}>
                      <label style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '6px', display: 'block' }}>Giriş Türü:</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          className={`mode-toggle-btn ${paymentInputMode === 'tl' ? 'active' : ''}`}
                          onClick={() => { setPaymentInputMode('tl'); setPaymentAmount(''); }}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: paymentInputMode === 'tl' ? '2px solid #3b82f6' : '1px solid #d1d5db',
                            borderRadius: '8px',
                            background: paymentInputMode === 'tl' ? 'linear-gradient(135deg, #dbeafe, #bfdbfe)' : '#f9fafb',
                            color: paymentInputMode === 'tl' ? '#1e40af' : '#6b7280',
                            fontWeight: paymentInputMode === 'tl' ? '600' : '400',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                          }}
                        >
                          ₺ TL Gir
                        </button>
                        <button
                          type="button"
                          className={`mode-toggle-btn ${paymentInputMode === 'gram' ? 'active' : ''}`}
                          onClick={() => { setPaymentInputMode('gram'); setPaymentAmount(''); }}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: paymentInputMode === 'gram' ? '2px solid #f59e0b' : '1px solid #d1d5db',
                            borderRadius: '8px',
                            background: paymentInputMode === 'gram' ? 'linear-gradient(135deg, #fef3c7, #fde68a)' : '#f9fafb',
                            color: paymentInputMode === 'gram' ? '#92400e' : '#6b7280',
                            fontWeight: paymentInputMode === 'gram' ? '600' : '400',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                          }}
                        >
                          ⚖️ Gram Gir
                        </button>
                      </div>
                    </div>
                  )}
                  <label>
                    {paymentInputMode === 'gram' && (selectedDebt.debt_type === 'GRAM' || selectedDebt.debt_type === 'GUMUS')
                      ? `Ödeme Miktarı (gr ${selectedDebt.debt_type === 'GRAM' ? 'Altın' : 'Gümüş'})`
                      : 'Ödeme Tutarı (₺)'}
                  </label>
                  {paymentInputMode === 'gram' && (selectedDebt.debt_type === 'GRAM' || selectedDebt.debt_type === 'GUMUS') ? (
                    <input
                      type="number"
                      step="0.001"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder="Gram miktarı girin..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') addPayment();
                        if (e.key === 'Tab' && !paymentAmount) {
                          e.preventDefault();
                          const gramRemaining = parseFloat(selectedDebt.amount) - mixedPayments.reduce((sum, p) => sum + (p.gramAmount || 0), 0);
                          setPaymentAmount(gramRemaining > 0 ? gramRemaining.toFixed(selectedDebt?.debt_type === 'GUMUS' ? 2 : 3) : '');
                        }
                      }}
                    />
                  ) : (
                    <PriceInput
                      value={parseFloat(paymentAmount) || 0}
                      onChange={(val) => setPaymentAmount(val)}
                      placeholder="Tutar girin..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') addPayment();
                        if (e.key === 'Tab' && !paymentAmount) {
                          e.preventDefault();
                          const remaining = getRemainingDebt();
                          if (remaining > 0) setPaymentAmount(remaining);
                        }
                      }}
                    />
                  )}
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}><Lightbulb size={12} color="#00bfa6" /> Tab ile tüm tutarı doldur</span>
                </div>
              )}

              {/* Gram karşılığı göster (TL modunda) */}
              {!scrapPaymentMode && paymentInputMode === 'tl' && (selectedDebt.debt_type === 'GRAM' || selectedDebt.debt_type === 'GUMUS') && paymentAmount && calculateMarginRate() > 0 && (
                <div className="gram-equivalent-info">
                  <span>Düşülecek Miktar:</span>
                  <strong>{calculateGramEquivalent().toFixed(selectedDebt.debt_type === 'GUMUS' ? 2 : 3)} gr</strong>
                  <span className="remaining-info">
                    (Kalan: {Math.max(0, Number(selectedDebt.amount) - calculateGramEquivalent()).toFixed(selectedDebt.debt_type === 'GUMUS' ? 2 : 3)} gr)
                  </span>
                </div>
              )}

              {/* TL karşılığı göster (Gram modunda) */}
              {!scrapPaymentMode && paymentInputMode === 'gram' && (selectedDebt.debt_type === 'GRAM' || selectedDebt.debt_type === 'GUMUS') && paymentAmount && calculateMarginRate() > 0 && (
                <div className="gram-equivalent-info" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
                  <span>TL Karşılığı:</span>
                  <strong>₺{(parseFloat(paymentAmount) * calculateMarginRate()).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                  <span className="remaining-info">
                    (Kalan: {Math.max(0, Number(selectedDebt.amount) - parseFloat(paymentAmount || 0)).toFixed(selectedDebt.debt_type === 'GUMUS' ? 2 : 3)} gr)
                  </span>
                </div>
              )}

              {getRemainingDebt() > 0.01 && <div className="form-group">
                <label>Ödeme Yöntemi</label>
                <div className="payment-methods">
                  <button
                    className={`payment-method-btn ${paymentMethod === 'Nakit' && !scrapPaymentMode ? 'active' : ''}`}
                    onClick={() => {
                      const amt = parseFloat(paymentAmount);
                      if (amt > 0) { addPayment('Nakit'); }
                      else { setPaymentMethod('Nakit'); setScrapPaymentMode(false); }
                    }}
                  >
                    <Banknote size={16} /> Nakit
                  </button>
                  <button
                    className={`payment-method-btn ${paymentMethod === 'Kredi Kartı' && !scrapPaymentMode ? 'active' : ''}`}
                    onClick={() => {
                      const amt = parseFloat(paymentAmount);
                      if (amt > 0) { addPayment('Kredi Kartı'); }
                      else { setPaymentMethod('Kredi Kartı'); setScrapPaymentMode(false); }
                    }}
                  >
                    <CreditCard size={16} /> Kredi Kartı
                  </button>
                  <button
                    className={`payment-method-btn ${paymentMethod === 'Havale/EFT' && !scrapPaymentMode ? 'active' : ''}`}
                    onClick={() => { setPaymentMethod('Havale/EFT'); setScrapPaymentMode(false); }}
                  >
                    <Building2 size={16} /> Havale/EFT
                  </button>
                  <button
                    className={`payment-method-btn scrap ${scrapPaymentMode ? 'active' : ''}`}
                    onClick={() => {
                      setScrapPaymentMode(true);
                      setPaymentMethod('Hurda');
                      setScrapPaymentData(prev => ({
                        ...prev,
                        purity: isGoldStore ? '22' : '925',
                        unit_price: getScrapAlisRate().toFixed(2)
                      }));
                    }}
                  >
                    <Scale size={16} /> {isGoldStore ? 'Altın Hurda' : 'Gümüş Hurda'}
                  </button>
                </div>
              </div>}

              {/* Havale/EFT Banka Hesapları */}
              {paymentMethod === 'Havale/EFT' && !scrapPaymentMode && (
                <div className="form-group">
                  <label>Banka Hesabı Seçin</label>
                  <div className="collection-bank-accounts">
                    {bankAccounts.length === 0 ? (
                      <p className="no-accounts-text">Kayıtlı banka hesabı bulunamadı.</p>
                    ) : (
                      bankAccounts.map(account => (
                        <div
                          key={account.id}
                          className={`bank-account-option ${selectedBankAccount?.id === account.id ? 'selected' : ''}`}
                          onClick={() => setSelectedBankAccount(account)}
                        >
                          <span className="account-icon">
                            {account.account_type === 'company' ? '🏢' : '👤'}
                          </span>
                          <span className="account-name">{account.account_name}</span>
                          {account.is_default === 1 && <span className="default-tag">Varsayılan</span>}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Hurda Formu */}
              {scrapPaymentMode && (
                <div className="collection-scrap-form">
                  <div className="scrap-form-row">
                    <div className="scrap-form-field">
                      <label>Has Gram</label>
                      <input
                        type="number"
                        step="0.01"
                        value={scrapPaymentData.gross_weight}
                        onChange={(e) => setScrapPaymentData(prev => ({ ...prev, gross_weight: e.target.value }))}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="scrap-form-field">
                      <label>Fire (gr)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={scrapPaymentData.waste_gram}
                        onChange={(e) => setScrapPaymentData(prev => ({ ...prev, waste_gram: e.target.value }))}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="scrap-form-row">
                    <div className="scrap-form-field ayar-field">
                      <label>Ayar</label>
                      <div className="ayar-select-wrapper">
                        <select
                          value={scrapPaymentData.purity}
                          onChange={(e) => {
                            setScrapPaymentData(prev => ({ ...prev, purity: e.target.value, custom_purity_rate: null }));
                            setIsEditingPurityRate(false);
                          }}
                        >
                          {isGoldStore ? (
                            <>
                              <option value="24">24 Ayar</option>
                              <option value="22">22 Ayar</option>
                              <option value="21">21 Ayar</option>
                              <option value="18">18 Ayar</option>
                              <option value="14">14 Ayar</option>
                              <option value="10">10 Ayar</option>
                              <option value="9">9 Ayar</option>
                              <option value="8">8 Ayar</option>
                            </>
                          ) : (
                            <>
                              <option value="999">999 Ayar</option>
                              <option value="925">925 Ayar</option>
                              <option value="900">900 Ayar</option>
                              <option value="800">800 Ayar</option>
                            </>
                          )}
                        </select>
                        {isGoldStore && (
                          <>
                            <span className="purity-rate-label">Oran:</span>
                            {isEditingPurityRate ? (
                              <input
                                type="number"
                                className="purity-rate-input"
                                value={editingPurityRateValue}
                                onChange={(e) => setEditingPurityRateValue(e.target.value)}
                                onBlur={() => {
                                  updatePurityRate('gold', scrapPaymentData.purity, editingPurityRateValue);
                                  setIsEditingPurityRate(false);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updatePurityRate('gold', scrapPaymentData.purity, editingPurityRateValue);
                                    setIsEditingPurityRate(false);
                                  }
                                  if (e.key === 'Escape') setIsEditingPurityRate(false);
                                }}
                                autoFocus
                              />
                            ) : (
                              <span
                                className="purity-rate-display"
                                onDoubleClick={() => {
                                  const currentRate = scrapPaymentData.custom_purity_rate || scrapPurityRates.gold?.[scrapPaymentData.purity] || defaultPurityRates.gold?.[scrapPaymentData.purity] || 0;
                                  setEditingPurityRateValue(Math.round(currentRate * 1000).toString());
                                  setIsEditingPurityRate(true);
                                }}
                                title="Çift tıkla düzenle"
                              >
                                {Math.round((scrapPaymentData.custom_purity_rate || scrapPurityRates.gold?.[scrapPaymentData.purity] || defaultPurityRates.gold?.[scrapPaymentData.purity] || 0) * 1000)}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="scrap-form-field">
                      <label>Gram Fiyatı (₺)</label>
                      <PriceInput
                        value={scrapPaymentData.unit_price}
                        onChange={(val) => setScrapPaymentData(prev => ({ ...prev, unit_price: val}))}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  {/* Hesaplama Özeti */}
                  {(() => {
                    const calc = calculateScrapPaymentValue();
                    return calc.totalValue > 0 ? (
                      <div className="scrap-calculation-summary">
                        <div className="calc-row">
                          <span>Saf Gram:</span>
                          <strong>{calc.pureWeight.toFixed(2)} gr</strong>
                        </div>
                        <div className="calc-row total">
                          <span>Hurda Değeri:</span>
                          {isEditingScrapTotal ? (
                            <PriceInput
                              className="edit-total-input"
                              value={editingScrapTotalValue}
                              onChange={(val) => setEditingScrapTotalValue(val)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const newValue = parseFloat(editingScrapTotalValue);
                                  if (!isNaN(newValue) && newValue >= 0) {
                                    setCustomScrapTotal(newValue);
                                  }
                                  setIsEditingScrapTotal(false);
                                }
                                if (e.key === 'Escape') setIsEditingScrapTotal(false);
                              }}
                              autoFocus
                            />
                          ) : (
                            <strong
                              className="editable-total"
                              onDoubleClick={() => {
                                setEditingScrapTotalValue(calc.totalValue.toFixed(2));
                                setIsEditingScrapTotal(true);
                              }}
                              title="Çift tıkla düzenle"
                              style={{ cursor: 'pointer' }}
                            >
                              ₺{calc.totalValue.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                              {customScrapTotal !== null && <span className="manual-badge">Manuel</span>}
                            </strong>
                          )}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setShowPaymentModal(false);
                setMixedPayments([]);
                setSelectedBankAccount(null);
                setScrapPaymentData(prev => ({...prev, custom_purity_rate: null}));
              }}>İptal</button>
              <button
                className="btn-save"
                onClick={() => setShowCollectionStaffModal(true)}
                disabled={!scrapPaymentMode && mixedPayments.length === 0}
              >
                {mixedPayments.length > 0
                  ? `Tahsilatı Tamamla (₺${getPaymentsTotal().toLocaleString('tr-TR', {minimumFractionDigits: 0})})`
                  : 'Ödemeyi Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tahsilat Kasiyer Seçim Modalı */}
      {showCollectionStaffModal && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Tahsilat Kasiyeri</h2>
              <button className="close-btn" onClick={() => setShowCollectionStaffModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="staff-list">
                {salesStaffList.map(staff => {
                  const isSelected = collectionStaffId === staff.id;
                  return (
                    <div key={staff.id} className={`staff-item ${isSelected ? 'selected' : ''}`} onClick={() => { setCollectionStaffId(staff.id); setCollectionStaffName(staff.name); }}>
                      <div className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}>
                        {isSelected ? <Check size={24} strokeWidth={3} /> : staff.photo_url ? <img src={`${API_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : staff.name.charAt(0)}
                      </div>
                      <div className="staff-info"><span className="staff-name">{staff.name}</span></div>
                    </div>
                  );
                })}
              </div>
              <div className="staff-modal-actions">
                <button className="confirm-staff-btn" disabled={!collectionStaffId} onClick={() => { setShowCollectionStaffModal(false); markDebtAsPaid(); }}>Tahsilatı Onayla</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Yardım Modal */}
      {showHelpModal && (
        <div className={`modal-overlay ${closingModal === 'help' ? 'closing' : ''}`}>
          <div className="modal help-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header help-header">
              <h2><BookOpen size={20} style={{ marginRight: '8px' }} /> Müşteri Yönetimi Yardım</h2>
              <button className="close-btn" onClick={() => handleCloseModal('help', setShowHelpModal)}>✕</button>
            </div>
            <div className="help-modal-content">
              {/* Genel Bilgi */}
              <div className="help-section">
                <h3><Users size={18} style={{ marginRight: '6px' }} /> Genel Bilgi</h3>
                <p>Bu sayfa üzerinden müşteri kayıtlarını yönetebilir, borç takibi yapabilir ve satış geçmişlerini görüntüleyebilirsiniz.</p>
              </div>

              {/* Yeni Müşteri */}
              <div className="help-section">
                <h3><Plus size={18} style={{ marginRight: '6px' }} /> Yeni Müşteri Ekleme</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span><strong>+ Yeni Müşteri</strong> butonuna tıklayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Ad Soyad ve Telefon bilgilerini girin (zorunlu)</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>İsteğe bağlı: Email, Adres, Vergi No ekleyin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span><strong>Kaydet</strong> butonuna tıklayın</span>
                  </div>
                </div>
              </div>

              {/* Bakiye Takibi */}
              <div className="help-section">
                <h3><Coins size={18} style={{ marginRight: '6px' }} /> Bakiye Takibi</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span>Borçlu müşteriler <strong>kırmızı</strong> satırla gösterilir</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span><strong>TL Bakiye:</strong> Artı (+) borç, Eksi (-) alacak gösterir</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span><strong>Gram Borç:</strong> Altın ve gümüş borçları ayrı gösterir</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span><strong><Coins size={14} /></strong> ikonuna tıklayarak bakiye detayını görün ve tahsilat yapın</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> TL Bakiye veya Gram Borç sütununa tıklayarak müşterileri sıralayabilirsiniz.</div>
              </div>

              {/* Excel İşlemleri */}
              <div className="help-section">
                <h3><FileSpreadsheet size={18} style={{ marginRight: '6px' }} /> Excel İşlemleri</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num"><FileSpreadsheet size={16} /></span>
                    <span><strong>Şablon:</strong> Örnek Excel şablonunu indirin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num"><Upload size={16} /></span>
                    <span><strong>İçe Aktar:</strong> Excel dosyasından toplu müşteri ekleyin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num"><Download size={16} /></span>
                    <span><strong>Dışa Aktar:</strong> Tüm müşterileri Excel'e indirin</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Excel formatı: Mağaza, Ad Soyad, Telefon, E-posta, Borç, Para Birimi (TL/ALTIN/GÜMÜŞ)</div>
              </div>

              {/* Butonlar */}
              <div className="help-section">
                <h3><CircleDot size={18} style={{ marginRight: '6px' }} /> Buton Açıklamaları</h3>
                <div className="help-buttons-grid">
                  <div className="help-button-item">
                    <span className="btn-icon"><Plus size={18} /></span>
                    <div>
                      <strong>Yeni Müşteri</strong>
                      <p>Yeni müşteri kaydı oluşturur</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><ScrollText size={18} /></span>
                    <div>
                      <strong>İşlem Geçmişi</strong>
                      <p>Tüm işlem loglarını gösterir</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Pencil size={18} /></span>
                    <div>
                      <strong>Düzenle</strong>
                      <p>Müşteri bilgilerini günceller</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><ClipboardList size={18} /></span>
                    <div>
                      <strong>Satış Geçmişi</strong>
                      <p>Müşterinin tüm alışverişlerini gösterir</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Coins size={18} /></span>
                    <div>
                      <strong>Bakiye Detayı</strong>
                      <p>Müşterinin bakiye ve borç detaylarını listeler</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Settings size={18} /></span>
                    <div>
                      <strong>Sütunlar</strong>
                      <p>Görünür sütunları ayarlar</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mağaza Filtreleme */}
              <div className="help-section">
                <h3><Store size={18} style={{ marginRight: '6px' }} /> Mağaza Filtreleme</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">🥇</span>
                    <span><strong>Altın:</strong> Altın mağazası müşterilerini listeler</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">🥈</span>
                    <span><strong>Gümüş:</strong> Gümüş mağazası müşterilerini listeler</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">🛒</span>
                    <span><strong>Shopify:</strong> Online mağaza müşterilerini görüntüler</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Üst kartlara tıklayarak mağaza tipine göre filtreleyebilirsiniz.</div>
              </div>

              {/* İpuçları */}
              <div className="help-section tips-section">
                <h3><Lightbulb size={18} style={{ marginRight: '6px', color: '#f59e0b' }} /> İpuçları</h3>
                <ul className="help-tips-list">
                  <li>Arama kutusu ile hızlıca müşteri bulun</li>
                  <li>TL Bakiye veya Gram Borç sütununa tıklayarak sıralayın</li>
                  <li>Sütunlar menüsünden görünümü özelleştirin</li>
                  <li>Doğum tarihi kaydedin - kampanyalar otomatik uygulanır</li>
                  <li>Vergi No ile kurumsal müşterileri takip edin</li>
                  <li>Sayfa sonuna kaydırınca otomatik yükler</li>
                  <li>Shopify müşterileri otomatik senkronize edilir</li>
                  <li>Müşteri tipini (Altın/Gümüş) kayıt sırasında belirleyin</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Excel İçe Aktarma Ön İzleme Modal */}
      {showImportPreview && importPreviewData && (
        <div className={`modal-overlay ${closingModal === 'importPreview' ? 'closing' : ''}`}>
          <div className="modal import-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FileSpreadsheet size={20} style={{ marginRight: '8px' }} /> İçe Aktarma Ön İzleme</h2>
              <button className="close-btn" onClick={() => handleCloseModal('importPreview', setShowImportPreview, () => { setImportPreviewData(null); setParsedCustomers([]); })}>✕</button>
            </div>
            <div className="import-preview-content">
              {/* Özet */}
              <div className="import-preview-summary">
                <div className="preview-stat new">
                  <Plus size={18} />
                  <span><strong>{importPreviewData.summary.newCount}</strong> yeni müşteri eklenecek</span>
                </div>
                <div className="preview-stat update">
                  <Pencil size={18} />
                  <span><strong>{importPreviewData.summary.updateCount}</strong> müşteri güncellenecek</span>
                </div>
                {importPreviewData.summary.noChangeCount > 0 && (
                  <div className="preview-stat nochange">
                    <CheckCircle size={18} />
                    <span><strong>{importPreviewData.summary.noChangeCount}</strong> müşteri zaten güncel</span>
                  </div>
                )}
              </div>

              {/* Güncellenecek Müşteriler */}
              {importPreviewData.updateCustomers.length > 0 && (
                <div className="import-preview-section">
                  <h3><Pencil size={16} /> Güncellenecek Müşteriler ({importPreviewData.updateCustomers.length})</h3>
                  <div className="import-preview-table-wrapper">
                    <table className="import-preview-table">
                      <thead>
                        <tr>
                          <th>Telefon</th>
                          <th>Alan</th>
                          <th>Mevcut Değer</th>
                          <th>Yeni Değer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreviewData.updateCustomers.map((item, idx) => (
                          Object.entries(item.changes).map(([field, change], fIdx) => (
                            <tr key={`${idx}-${fIdx}`}>
                              {fIdx === 0 && (
                                <td rowSpan={Object.keys(item.changes).length} className="preview-phone-cell">
                                  <Phone size={14} /> {item.existing.phone}
                                </td>
                              )}
                              <td className="preview-field-name">
                                {field === 'name' ? 'Ad Soyad' : field === 'store_type' ? 'Mağaza' : field === 'email' ? 'E-posta' : field}
                              </td>
                              <td className="preview-old-value">{change.old || '-'}</td>
                              <td className="preview-new-value">{change.new}</td>
                            </tr>
                          ))
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Yeni Eklenecek Müşteriler */}
              {(importPreviewData.newCustomers.length > 0 || importPreviewData.noPhoneCustomers.length > 0) && (
                <div className="import-preview-section">
                  <h3><Plus size={16} /> Yeni Eklenecek Müşteriler ({importPreviewData.newCustomers.length + importPreviewData.noPhoneCustomers.length})</h3>
                  <div className="import-preview-table-wrapper">
                    <table className="import-preview-table">
                      <thead>
                        <tr>
                          <th>Ad Soyad</th>
                          <th>Telefon</th>
                          <th>Mağaza</th>
                          <th>E-posta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...importPreviewData.newCustomers, ...importPreviewData.noPhoneCustomers].map((customer, idx) => (
                          <tr key={idx}>
                            <td>{customer.name}</td>
                            <td>{customer.phone || '-'}</td>
                            <td>{customer.store_type || '-'}</td>
                            <td>{customer.email || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="import-preview-actions">
              <button
                className="btn secondary"
                onClick={() => handleCloseModal('importPreview', setShowImportPreview, () => { setImportPreviewData(null); setParsedCustomers([]); })}
                disabled={importing}
              >
                İptal
              </button>
              <button
                className="btn primary"
                onClick={handleConfirmImport}
                disabled={importing}
              >
                {importing ? 'İçe aktarılıyor...' : `Onayla ve İçe Aktar (${importPreviewData.summary.newCount + importPreviewData.summary.updateCount} müşteri)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Modal */}
      <LogModal
        isOpen={showLogModal}
        onClose={() => setShowLogModal(false)}
        module="customers"
        title="Müşteri Yönetimi"
        showEntityCode={false}
      />

      {/* KVKK Aydınlatma Metni Modal */}
      {showKvkkModal && (
        <div className="modal-overlay">
          <div className="modal kvkk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>KVKK Aydınlatma Metni</h2>
              <button className="close-btn" onClick={() => setShowKvkkModal(false)}>✕</button>
            </div>
            <div className="modal-body kvkk-content">
              <h3>KİŞİSEL VERİLERİN KORUNMASI KANUNU KAPSAMINDA AYDINLATMA METNİ</h3>

              <p><strong>Veri Sorumlusu:</strong> AKIK Kuyumculuk</p>

              <h4>1. Kişisel Verilerin İşlenme Amacı</h4>
              <p>Kişisel verileriniz aşağıdaki amaçlarla işlenmektedir:</p>
              <ul>
                <li>Müşteri ilişkileri yönetimi ve iletişim faaliyetlerinin yürütülmesi</li>
                <li>Satış ve satış sonrası hizmetlerin sunulması</li>
                <li>Kampanya, promosyon ve duyuruların iletilmesi (onay verilmesi halinde)</li>
                <li>Yasal yükümlülüklerin yerine getirilmesi</li>
                <li>Fatura ve ödeme işlemlerinin gerçekleştirilmesi</li>
              </ul>

              <h4>2. İşlenen Kişisel Veriler</h4>
              <ul>
                <li>Kimlik bilgileri (ad, soyad, T.C. kimlik numarası)</li>
                <li>İletişim bilgileri (telefon numarası, e-posta adresi, adres)</li>
                <li>Müşteri işlem bilgileri (satın alma geçmişi, ödeme bilgileri)</li>
                <li>Doğum tarihi (özel gün kutlamaları için)</li>
              </ul>

              <h4>3. Kişisel Verilerin Aktarılması</h4>
              <p>Kişisel verileriniz; yasal zorunluluklar kapsamında yetkili kamu kurum ve kuruluşlarına,
              ödeme işlemleri için iş birliği yapılan finans kuruluşlarına ve SMS/e-posta gönderimi için
              hizmet alınan tedarikçilere aktarılabilmektedir.</p>

              <h4>4. Kişisel Veri Toplamanın Yöntemi ve Hukuki Sebebi</h4>
              <p>Kişisel verileriniz, mağazamızda gerçekleştirilen satış işlemleri sırasında,
              sözlü veya yazılı olarak tarafınızdan alınmaktadır. Bu veriler; sözleşmenin kurulması
              ve ifası, yasal yükümlülüklerin yerine getirilmesi ve açık rızanız kapsamında işlenmektedir.</p>

              <h4>5. Kişisel Veri Sahibinin Hakları</h4>
              <p>KVKK'nın 11. maddesi uyarınca aşağıdaki haklara sahipsiniz:</p>
              <ul>
                <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
                <li>İşlenmişse buna ilişkin bilgi talep etme</li>
                <li>İşlenme amacını ve amaca uygun kullanılıp kullanılmadığını öğrenme</li>
                <li>Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme</li>
                <li>Eksik veya yanlış işlenmişse düzeltilmesini isteme</li>
                <li>KVKK'nın 7. maddesinde öngörülen şartlar çerçevesinde silinmesini veya yok edilmesini isteme</li>
                <li>İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle
                aleyhinize bir sonucun ortaya çıkmasına itiraz etme</li>
                <li>Kanuna aykırı olarak işlenmesi sebebiyle zarara uğramanız halinde zararın giderilmesini talep etme</li>
              </ul>

              <h4>6. İletişim</h4>
              <p>Haklarınızı kullanmak için mağazamıza yazılı olarak başvurabilirsiniz.</p>

              <p className="kvkk-date"><em>Son güncelleme: {new Date().toLocaleDateString('tr-TR')}</em></p>
            </div>
            <div className="modal-footer">
              <button className="btn-save" onClick={() => setShowKvkkModal(false)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Genel Onay Modal (window.confirm yerine) */}
      {showConfirmModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-header" style={{
              background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                         confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                         'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={28} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{confirmModalConfig.title}</h2>
            </div>
            <div className="modal-body" style={{ padding: '24px', background: '#fff' }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#374151', lineHeight: '1.6' }}>
                {confirmModalConfig.message}
              </p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px',
              background: '#f9fafb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.cancelText}
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  confirmModalConfig.onConfirm();
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                             confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                             'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Rapor Seçim Modalı */}
      {showReportModal && (
        <div className="modal-overlay" onClick={() => !generatingReport && setShowReportModal(false)}>
          <div className="modal" style={{ maxWidth: '360px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Rapor Seçin</h2>
              <button className="close-btn" onClick={() => !generatingReport && setShowReportModal(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={generateDebtReport}
                disabled={generatingReport}
                style={{
                  padding: '18px 20px',
                  borderRadius: '12px',
                  border: '2px solid #fecaca',
                  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                  color: '#dc2626',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: generatingReport ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.2s'
                }}
              >
                <Printer size={20} />
                {generatingReport ? 'Oluşturuluyor...' : 'Borç Raporu'}
              </button>
              <button
                onClick={generateCreditReport}
                disabled={generatingReport}
                style={{
                  padding: '18px 20px',
                  borderRadius: '12px',
                  border: '2px solid #a7f3d0',
                  background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                  color: '#16a34a',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: generatingReport ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.2s'
                }}
              >
                <Printer size={20} />
                {generatingReport ? 'Oluşturuluyor...' : 'Alacak Raporu'}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>{/* main-content kapanışı */}

      {/* Fiş Detay Modal */}
      {(saleDetailModal || saleDetailLoading) && (
        <div className="modal-overlay" onClick={() => { setSaleDetailModal(null); setSaleDetailLoading(false); }}>
          <div className="modal sale-detail-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px', width: '95%' }}>
            {saleDetailLoading && !saleDetailModal ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>Yükleniyor...</div>
            ) : saleDetailModal ? (
              <>
                <div className="modal-header">
                  <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span>Fiş Detayı - #{saleDetailModal.receipt_number || saleDetailModal.store_sale_number || saleDetailModal.id}</span>
                    {saleDetailModal.sale_type === 'refund' && (
                      <span style={{ backgroundColor: '#ef4444', color: 'white', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' }}>İADE FİŞİ</span>
                    )}
                  </h2>
                  <button className="close-btn" onClick={() => setSaleDetailModal(null)}>✕</button>
                </div>
                <div className="modal-body">
                  {/* Bilgiler */}
                  <div className="sale-detail-info">
                    <div className="sale-detail-row">
                      <span className="sale-detail-label">Tarih</span>
                      <span className="sale-detail-value">{new Date(saleDetailModal.created_at).toLocaleDateString('tr-TR')} {new Date(saleDetailModal.created_at).toLocaleTimeString('tr-TR')}</span>
                    </div>
                    <div className="sale-detail-row">
                      <span className="sale-detail-label">Müşteri</span>
                      <span className="sale-detail-value">{saleDetailModal.customer_name || 'Müşteri Yok'}</span>
                    </div>
                    <div className="sale-detail-row">
                      <span className="sale-detail-label">Ödeme Yöntemi</span>
                      <span className="sale-detail-value">
                        {(() => {
                          let details = saleDetailModal.payment_details;
                          let paymentList = [];
                          if (details && (Array.isArray(details) ? details.length > 0 : true)) {
                            paymentList = Array.isArray(details) ? details : (typeof details === 'string' ? JSON.parse(details) : []);
                          }
                          if (paymentList.length === 0 && saleDetailModal.payment_method) {
                            return saleDetailModal.payment_method;
                          }
                          if (paymentList.length > 0) {
                            return (
                              <div className="payment-details-list">
                                {paymentList.map((p, idx) => {
                                  const amount = p.currency === 'TRY' || !p.currency
                                    ? `₺${Number(p.amount_tl || p.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                                    : `${p.amount} ${p.currency}`;
                                  return (
                                    <div key={idx} className="payment-detail-item">
                                      <span className="payment-method-tag">{(p.method || '').replace('Açık Hesap (TL)', 'Açık Hesap (₺)')}</span>
                                      <span className="payment-amount">{amount}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                          return '-';
                        })()}
                      </span>
                    </div>
                    <div className="sale-detail-row">
                      <span className="sale-detail-label">Satış Sorumlusu</span>
                      <span className="sale-detail-value">{saleDetailModal.staff_name || '-'}</span>
                    </div>
                    {(saleDetailModal.gold_rate || saleDetailModal.silver_rate) && (
                      <div className="sale-detail-row">
                        <span className="sale-detail-label">Kur</span>
                        <span className="sale-detail-value">
                          {saleDetailModal.gold_rate && <span style={{ marginRight: '10px' }}>Altın: ₺{Number(saleDetailModal.gold_rate).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>}
                          {saleDetailModal.silver_rate && <span>Gümüş: ₺{Number(saleDetailModal.silver_rate).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Ürünler Tablosu */}
                  {saleDetailModal.items && saleDetailModal.items.length > 0 && (
                    <div className="sale-detail-items">
                      <table className="sale-items-table">
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left' }}>Ürün</th>
                            <th style={{ textAlign: 'center' }}>Adet</th>
                            <th style={{ textAlign: 'right' }}>Birim Fiyat</th>
                            <th style={{ textAlign: 'right' }}>İndirim</th>
                            <th style={{ textAlign: 'right' }}>Toplam</th>
                          </tr>
                        </thead>
                        <tbody>
                          {saleDetailModal.items.map((item, idx) => {
                            const origPrice = Number(item.original_unit_price || item.unit_price) || 0;
                            const discPercent = Number(item.discount_percent) || 0;
                            const discAmount = Number(item.discount_amount) || 0;
                            const hasDiscount = discPercent > 0 || discAmount > 0;
                            return (
                              <tr key={idx}>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {item.image && (
                                      <img
                                        src={item.image.startsWith('http') ? item.image : `${API_HOST}${item.image}`}
                                        alt=""
                                        onError={e => { e.target.style.display = 'none'; }}
                                        onClick={e => { e.stopPropagation(); setLightboxImage(item.image.startsWith('http') ? item.image : `${API_HOST}${item.image}`); }}
                                        style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover', cursor: 'pointer', border: '1px solid #e5e7eb' }}
                                      />
                                    )}
                                    <div>
                                      <div style={{ fontWeight: 500 }}>{item.product_name}</div>
                                      {item.barcode && <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{item.barcode}</div>}
                                      {item.note && <div style={{ fontSize: '0.75rem', color: '#f59e0b' }}>📝 {item.note}</div>}
                                    </div>
                                  </div>
                                </td>
                                <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                                <td style={{ textAlign: 'right', textDecoration: hasDiscount ? 'line-through' : 'none', color: hasDiscount ? '#94a3b8' : '#1e293b' }}>₺{origPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                                <td style={{ textAlign: 'right', color: '#dc2626' }}>
                                  {hasDiscount ? (discPercent > 0 ? `%${discPercent.toFixed(1)}` : `-₺${discAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`) : '-'}
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 600 }}>₺{Number(item.total_price).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Alt Toplam */}
                  <div className="sale-detail-totals">
                    {Number(saleDetailModal.discount) > 0 && (
                      <div className="sale-detail-row">
                        <span className="sale-detail-label">Ara Toplam</span>
                        <span className="sale-detail-value">₺{Number(saleDetailModal.subtotal).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {Number(saleDetailModal.discount) > 0 && (
                      <div className="sale-detail-row">
                        <span className="sale-detail-label" style={{ color: '#dc2626' }}>İndirim</span>
                        <span className="sale-detail-value" style={{ color: '#dc2626' }}>-₺{Number(saleDetailModal.discount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="sale-detail-row" style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      <span className="sale-detail-label">Toplam</span>
                      <span className="sale-detail-value">₺{Number(saleDetailModal.total).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>

                  {/* Perakende Satışta Aç Butonu */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                    <button
                      onClick={() => { setSaleDetailModal(null); navigate(`/retail-sales?sale=${saleDetailModal.id}`); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '8px 16px', borderRadius: '8px',
                        background: '#2563eb', color: '#fff', border: 'none',
                        cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem'
                      }}
                    >
                      <ExternalLink size={15} /> Perakende Satışta Aç
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Ürün Görseli Lightbox */}
      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <img
            src={lightboxImage}
            alt=""
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '12px', objectFit: 'contain', cursor: 'default' }}
          />
          <button
            onClick={() => setLightboxImage(null)}
            style={{
              position: 'absolute', top: '20px', right: '20px',
              background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
              fontSize: '1.5rem', width: '40px', height: '40px', borderRadius: '50%',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >✕</button>
        </div>
      )}
    </div>
  );
};

export default Customers;
